import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  makeStyles,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Menu,
  MenuItem,
  IconButton
} from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ReceiptIcon from '@material-ui/icons/Receipt';
import LanguageIcon from '@material-ui/icons/Language';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import html2pdf from 'html2pdf.js';
import InvoicePrintableContent from '../../../components/InvoicePrintableContent';

let stripePromise;

const getStripePromise = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_default');
  }
  return stripePromise;
};

// Brand colors matching PatientAppointments
const BRAND_COLORS = {
  PRIMARY: '#002D55',
  ACCENT: '#fb741a',
  SUCCESS: '#4caf50',
  ERROR: '#d32f2f',
  WARNING: '#ff9800',
  INFO: '#2196f3',
  DISABLED: '#9e9e9e',
  TEXT_TERTIARY: '#666666',
  BORDER: '#e0e0e0',
};

// ─── Language Translations ───────────────────────────────────────────────────
const translations = {
  en: {
    modalTitle: 'Add Payment Method',
    infoText: 'Set up your payment details securely with Stripe. Once saved, you can approve invoice payments with a single click when your physiotherapist sends payment requests. Any active subscriptions will automatically renew until cancelled.',
    securePayment: '🔒 Secure payment processing by Stripe',
    saveButton: 'Save Payment Method',
    cancelButton: 'Cancel',
    loadingError: 'Failed to load payment form',
  },
  da: {
    modalTitle: 'Tilføj Betalingsmetode',
    infoText: 'Konfigurer dine betalingsoplysninger sikkert med Stripe. Når de er gemt, kan du godkende fakturabetalinger med et enkelt klik, når din fysioterapeut sender betalingsanmodninger. Eventuelle aktive abonnementer fornyes automatisk, indtil de annulleres.',
    securePayment: '🔒 Sikker betalingsbehandling af Stripe',
    saveButton: 'Gem Betalingsmetode',
    cancelButton: 'Annuller',
    loadingError: 'Kunne ikke indlæse betalingsformular',
  }
};

// ─── Stripe Setup Form (inside Elements context) ──────────────────────────────
const SetupForm = ({ onSuccess, onCancel, language }) => {
  const t = translations[language];
  const stripe = useStripe();
  const elements = useElements();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      onSuccess(setupIntent);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Typography style={{ color: 'red', marginTop: 8, fontSize: 13 }}>
          {error}
        </Typography>
      )}
      <Box style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || loading}
          className={classes.Button}
          style={{ flex: 1 }}
        >
          {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : t.saveButton}
        </Button>
        <Button className={classes.closeIconButton} onClick={onCancel} disabled={loading} style={{ borderRadius: 18, padding: '6px 16px', fontWeight: 600 }}>
          {t.cancelButton}
        </Button>
      </Box>  
    </form>
  );
};

// ─── Modal: fetches clientSecret, wraps SetupForm in Elements ────────────────
const AddPaymentMethodModal = ({ open, onClose, onSuccess }) => {
  const token = useSelector(state => state.auth.token);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('en');
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
  const t = translations[language];

  useEffect(() => {
    if (!open) return;
    setClientSecret(null);
    setError(null);
    
    // Call backend to create SetupIntent
    fetch(`${backendBaseUrl}/api/profile/stripe/setup-intent/`, { 
      method: 'POST',
      headers: {
        'Authorization': `Token ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.client_secret) {
          setClientSecret(data.client_secret);
        } else {
          setError(data.error || t.loadingError);
        }
      })
      .catch((err) => {
        console.error('Setup intent error:', err);
        setError(t.loadingError);
      });
  }, [open, backendBaseUrl, token, t.loadingError]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ style: { borderRadius: 18 } }}>
      <DialogTitle style={{ color: '#002D55' }}>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <svg width="60" height="25" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z" fill="#635BFF"/>
            </svg>
            <Box style={{ width: 1, height: 24, backgroundColor: '#d0d0d0' }} />
            <span style={{ color: '#002D55', fontWeight: 600 }}>{t.modalTitle}</span>
          </Box>
          <Button
            size="small"
            onClick={() => setLanguage(language === 'en' ? 'da' : 'en')}
            style={{ minWidth: 60, padding: '4px 8px' }}
          >
            <LanguageIcon style={{ fontSize: 18, marginRight: 4 }} />
            {language === 'en' ? '🇩🇰 DA' : '🇬🇧 EN'}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent style={{ paddingBottom: 24 }}>
        {/* Informational text */}
        <Box style={{ 
          backgroundColor: '#f5f9ff', 
          padding: '12px 16px', 
          borderRadius: 4, 
          marginBottom: 16,
          border: '1px solid #e3f2fd'
        }}>
          <Typography variant="body2" style={{ color: '#555', lineHeight: 1.6, marginBottom: 8 }}>
            {t.infoText}
          </Typography>
          <Typography variant="caption" style={{ color: '#666', fontStyle: 'italic' }}>
            {t.securePayment}
          </Typography>
        </Box>
        
        {error && (
          <Typography style={{ color: 'red', marginBottom: 12 }}>
            {error}
          </Typography>
        )}
        {clientSecret ? (
          <Elements key={language} stripe={getStripePromise()} options={{ clientSecret, locale: language }}>
            <SetupForm onSuccess={onSuccess} onCancel={onClose} language={language} />
          </Elements>
        ) : (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Stripe Checkout Form Component ──────────────────────────────────────────
const StripeCheckoutForm = ({ paymentIntentSecret, amount, invoiceCount, onSubmit, loading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      setCardError('Stripe not loaded');
      return;
    }

    setCardError(null);
    
    // Step 1: Submit the elements to validate and prepare the payment
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setCardError(submitError.message);
      return;
    }
    
    // Step 2: Confirm the payment
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard`,
      },
      redirect: 'if_required'
    });

    if (error) {
      setCardError(error.message);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSubmit({ success: true, paymentIntent });
    } else if (paymentIntent) {
      onSubmit({ success: false, paymentIntent });
    }
  };

  return (
    <Box>
      <Typography style={{ color: '#666', marginBottom: 16 }}>
        Enter your card details to pay {invoiceCount} unpaid {invoiceCount === 1 ? 'invoice' : 'invoices'} securely with Stripe.
      </Typography>
      <Box style={{ padding: 12, borderRadius: 8, backgroundColor: '#F5F9FF', marginBottom: 16 }}>
        <Typography style={{ color: '#002D55', fontWeight: 600 }}>
          Invoices to charge: {invoiceCount}
        </Typography>
        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 16 }}>
          Total Due: EUR {amount.toFixed(2)}
        </Typography>
      </Box>

      <form onSubmit={handleFormSubmit}>
        <Box style={{
          padding: 16,
          backgroundColor: '#FFF',
          border: '1px solid #ddd',
          borderRadius: 4,
          marginBottom: 16
        }}>
          <PaymentElement
            options={{
              layout: 'tabs',
              defaultValues: {
                billingDetails: {
                  email: 'patient@example.com'
                }
              }
            }}
          />
          
          {cardError && (
            <Typography style={{ color: '#d32f2f', marginTop: 12, fontSize: 12 }}>
              ❌ {cardError}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !stripe || !elements}
            style={{
              backgroundColor: '#002D55',
              color: '#fff',
              height: 44,
              fontWeight: 600,
              marginTop: 16
            }}
          >
            {loading ? (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={20} style={{ color: '#fff' }} />
                Processing...
              </Box>
            ) : (
              `Pay EUR ${amount.toFixed(2)}`
            )}
          </Button>
        </Box>

        <Typography style={{ fontSize: 10, color: '#999', textAlign: 'center' }}>
          🔒 Secure payment processing by Stripe. Your card details are never stored on our servers.
        </Typography>
      </form>
    </Box>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
  orangeFocused: {
    backgroundColor: "#FFFFFF",
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#fb741a',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#fb741a',
    },
  },
  root: {
    background: "#f7faff",
    boxShadow: "0 6px 32px rgba(0,45,85,0.18)",
    borderRadius: 14,
  },
  Button: {
    color: "#FFFFFF",
    backgroundColor: "#002D55",
    borderRadius: 18,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.39vw, 6px) clamp(10px, 1.04vw, 16px)',
    marginRight: 'clamp(6px, 0.52vw, 8px)',
    fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: "#fb741a",
    }
  },
  card: {
    background: '#f7faff',
    borderRadius: 14,
  },
  cardContent: {
    background: '#f7faff',
    borderRadius: 14,
    padding: 24,
  },
  cardHeaderRoot: {
    height: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    minHeight: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    padding: '0 clamp(10px, calc(2px + 0.625vw), 16px)',
    boxSizing: 'border-box',
    borderBottom: '2px solid #fb741a',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    '& .MuiCardHeader-content': {
      overflow: 'hidden',
    },
  },
  cardHeaderTitle: {
    color: "#002D55",
    fontWeight: "bold",
    fontSize: 'clamp(15px, 1.4vw, 22px)',
    paddingLeft: 'clamp(5px, calc(2px + 0.625vw), 5px)',
  },
  cardHeaderSubTitle: {
    color: "#002D55",
    fontWeight: "bold",
    fontSize: 'clamp(15px, 1.4vw, 22px)',
  },
  bodyText: {
    color: "#666666",
    marginBottom: 12,
    fontSize: 'clamp(12px, 1vw, 15px)',
  },
  statusChip: {
    backgroundColor: "#E0E0E0",
    color: "#666666",
    marginTop: 8,
    fontSize: 'clamp(10px, 0.78vw, 12px)',
  },
  statusChipActive: {
    backgroundColor: "#d4edda",
    color: "#155724",
    marginTop: 8,
    fontSize: 'clamp(10px, 0.78vw, 12px)',
  },
  subscriptionRow: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 12,
  },
  subscriptionItem: {
    flex: 1,
    marginRight: 12,
  },
  subscriptionLabel: {
    color: "#002D55",
    fontWeight: '600',
    marginBottom: 4,
    fontSize: 'clamp(11px, 0.9vw, 14px)',
  },
  subscriptionValue: {
    color: "#666666",
    fontSize: 'clamp(11px, 0.9vw, 14px)',
  },
  closeIconButton: {
    //transition: 'background 0.2s',
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    fontSize: 'clamp(0.68rem, calc(2px + 0.722vw), 0.95rem)',
    borderRadius: 18,
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#fb741a',
      border: 'none',
      boxShadow: 'none',
    },
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────
const PaymentSubscription = ({ className, promptAutoPayRequest = false, ...rest }) => {
  const classes = useStyles();
  const reduxToken = useSelector(state => state.auth.token);
  const reduxUser = useSelector(state => state.auth.user);
  const [modalOpen, setModalOpen] = useState(false);
  const [cardSaved, setCardSaved] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [insuranceInfo, setInsuranceInfo] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [paymentPreference, setPaymentPreference] = useState('invoice');
  const [loadingPref, setLoadingPref] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [challengeInvoiceId, setChallengeInvoiceId] = useState(null);
  const [challengeReason, setChallengeReason] = useState('');
  const [acceptedInvoices, setAcceptedInvoices] = useState(new Set());
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [bulkActionsAnchor, setBulkActionsAnchor] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [cancellingAppointment, setCancellingAppointment] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [reschedulingAppointment, setReschedulingAppointment] = useState(false);
  const [newAppointmentDate, setNewAppointmentDate] = useState('');
  const [newAppointmentTime, setNewAppointmentTime] = useState('');
  const [newAppointmentType, setNewAppointmentType] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [consultationServices, setConsultationServices] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [fetchingTimes, setFetchingTimes] = useState(false);
  const [rescheduleConfirmHover, setRescheduleConfirmHover] = useState(false);
  const [rebokeButtonHover, setRebokeButtonHover] = useState(false);
  const [cancelConfirmDialogOpen, setCancelConfirmDialogOpen] = useState(false);
  const [unpaidFlowOpen, setUnpaidFlowOpen] = useState(false);
  const [unpaidCancelHover, setUnpaidCancelHover] = useState(false);
  const [unpaidProceedHover, setUnpaidProceedHover] = useState(false);
  const [unpaidFlowStep, setUnpaidFlowStep] = useState('overview');
  const [processingUnpaidFlow, setProcessingUnpaidFlow] = useState(false);
  const [paymentConfirmation, setPaymentConfirmation] = useState(null);
  const [appointmentInterval, setAppointmentInterval] = useState(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState(null);
  const [paymentIntentSecret, setPaymentIntentSecret] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [invoiceActionsAnchorEl, setInvoiceActionsAnchorEl] = useState(null);
  const [invoiceActionsInvoice, setInvoiceActionsInvoice] = useState(null);
  const [shouldPollInterval, setShouldPollInterval] = useState(true); // Will be set to false for patient users
  const [autoPayConsentOpen, setAutoPayConsentOpen] = useState(false);
  const [highlightAutoPayRequest, setHighlightAutoPayRequest] = useState(false);
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
  const token = reduxToken || '';
  const patientId = reduxUser && (reduxUser.id || reduxUser.user_id || reduxUser.userId || reduxUser.uuid);

  const denormalizeInsuranceProvider = (value) => {
    if (!value) return '—';
    const normalized = String(value).trim().toLowerCase();
    const map = {
      united_healthcare: 'United Healthcare',
      aetna: 'Aetna',
      cigna: 'Cigna',
      anthem: 'Anthem',
      humana: 'Humana',
      kaiser: 'Kaiser Permanente',
      private: 'Private Insurance',
      other: 'Other',
    };
    return map[normalized] || value;
  };

  const denormalizePlanType = (value) => {
    if (!value) return '—';
    const normalized = String(value).trim().toLowerCase();
    const map = {
      ppo: 'PPO',
      hmo: 'HMO',
      epo: 'EPO',
      pos: 'POS',
      private: 'Private',
      other: 'Other',
    };
    return map[normalized] || value;
  };

  // Fetch payment preference and check for saved card on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingPref(true);
        const res = await fetch(`${backendBaseUrl}/api/profile/payment-preference/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        if (!mounted) return;
        if (res.ok) {
          const data = await res.json();
          setPaymentPreference(data.preference || 'invoice');
        }
      } catch (e) {
        console.error('Error loading preference:', e);
      } finally {
        if (mounted) setLoadingPref(false);
      }
    })();

    // Check if patient has a card on file via Stripe
    (async () => {
      try {
        const res = await fetch(`${backendBaseUrl}/api/stripe/customer/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        if (!mounted) return;
        if (res.ok) {
          const data = await res.json();
          setCardSaved(!!data.has_payment_method);
        }
      } catch (e) {
        console.error('Error checking card on file:', e);
      }
    })();
    
    // Fetch appointment interval on mount
    fetchAppointmentInterval();
    
    // Set up polling to check for interval changes every 15 seconds (only for staff users)
    let intervalId;
    if (shouldPollInterval) {
      intervalId = setInterval(() => {
        fetchAppointmentInterval();
      }, 15000);
    }
    
    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [backendBaseUrl, token, shouldPollInterval]);

  // Fetch appointments and invoices
  useEffect(() => {
    if (!token) {
      console.log('No token available, skipping data fetch');
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoadingData(true);
        console.log('Fetching appointments from:', `${backendBaseUrl}/api/calendar/appointments/`);
        
        // Fetch treatments
        try {
          const treatRes = await fetch(`${backendBaseUrl}/treatment/user/treatments/`, {
            headers: { 'Authorization': `Token ${token}` }
          });
          if (treatRes.ok) {
            const treatData = await treatRes.json();
            const treatArray = Array.isArray(treatData)
              ? treatData
              : treatData.treatments || treatData.results || [];
            setTreatments(treatArray);
          }
        } catch (e) {
          console.error('Error fetching treatments:', e);
        }
        
        // Fetch appointments
        const apptRes = await fetch(`${backendBaseUrl}/api/calendar/appointments/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        
        console.log('Appointments response status:', apptRes.status);
        
        if (apptRes.ok && mounted) {
          const apptData = await apptRes.json();
          console.log('Appointments data received:', apptData);
          // Handle both formats: {appointments: [...]} or direct array or {results: [...]}
          const apptArray = apptData.appointments || (Array.isArray(apptData) ? apptData : apptData.results || []);
          console.log('Processed appointments array:', apptArray);
          setAppointments(apptArray);
        } else if (!apptRes.ok) {
          console.error('Failed to fetch appointments:', apptRes.status, apptRes.statusText);
          const errorText = await apptRes.text();
          console.error('Error response:', errorText);
        }
        
        // Fetch invoices
        if (mounted) {
          console.log('Fetching invoices from:', `${backendBaseUrl}/api/calendar/invoices/`);
          const invRes = await fetch(`${backendBaseUrl}/api/calendar/invoices/`, {
            headers: { 'Authorization': `Token ${token}` }
          });
          
          console.log('Invoices response status:', invRes.status);
          
          if (invRes.ok) {
            const invData = await invRes.json();
            console.log('Invoices data received:', invData);
            // Handle both formats: direct array or {results: [...]}
            const invArray = Array.isArray(invData) ? invData : invData.results || [];
            console.log('Processed invoices array:', invArray);
            setInvoices(invArray);
          } else {
            console.error('Failed to fetch invoices:', invRes.status, invRes.statusText);
            const errorText = await invRes.text();
            console.error('Error response:', errorText);
          }
        }
      } catch (e) {
        console.error('Error loading data:', e);
      } finally {
        if (mounted) setLoadingData(false);
      }
    })();
    return () => { mounted = false; };
  }, [backendBaseUrl, token]);

  useEffect(() => {
    if (!token || !patientId) {
      setInsuranceInfo(null);
      return;
    }

    let mounted = true;

    (async () => {
      try {
        const response = await fetch(`${backendBaseUrl}/profile/patient/${patientId}/insurance/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!mounted) return;

        if (response.status === 404) {
          setInsuranceInfo(null);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to load insurance (${response.status})`);
        }

        const data = await response.json();
        const insurance = Array.isArray(data) ? data[0] : (data?.results?.[0] || data);

        if (!insurance) {
          setInsuranceInfo(null);
          return;
        }

        setInsuranceInfo({
          provider: denormalizeInsuranceProvider(insurance.insurance_provider),
          planType: denormalizePlanType(insurance.plan_type),
          policyNumber: insurance.policy_number || '',
        });
      } catch (error) {
        console.error('Failed to load insurance for payment dashboard:', error);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [backendBaseUrl, token, patientId]);

  // Reset hover states when dialogs close
  useEffect(() => {
    if (!rescheduleDialogOpen) {
      setRescheduleConfirmHover(false);
    }
  }, [rescheduleDialogOpen]);

  useEffect(() => {
    if (!appointmentDialogOpen) {
      setRebokeButtonHover(false);
    }
  }, [appointmentDialogOpen]);

  const handlePreferenceChange = (pref) => {
    if (pref !== 'auto') {
      setHighlightAutoPayRequest(false);
    }
    if (pref === 'auto') {
      setAutoPayConsentOpen(true);
    } else {
      handleUpdatePreference(pref);
    }
  };

  const handleConfirmAutoPay = () => {
    setAutoPayConsentOpen(false);
    setHighlightAutoPayRequest(false);
    handleUpdatePreference('auto');
  };

  useEffect(() => {
    if (!promptAutoPayRequest || loadingPref) return;

    // Trigger consent modal immediately from patient notification deep-link.
    if (paymentPreference !== 'auto') {
      setHighlightAutoPayRequest(true);
      setAutoPayConsentOpen(true);
    }
  }, [promptAutoPayRequest, loadingPref, paymentPreference]);

  const handleUpdatePreference = async (pref) => {
    try {
      setPaymentPreference(pref);
      await fetch(`${backendBaseUrl}/api/profile/payment-preference/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preference: pref })
      });
    } catch (e) {
      console.error('Error updating preference:', e);
    }
  };

  const refreshAppointmentsAndInvoices = async () => {
    const [apptRes, invRes] = await Promise.all([
      fetch(`${backendBaseUrl}/api/calendar/appointments/`, {
        headers: { 'Authorization': `Token ${token}` }
      }),
      fetch(`${backendBaseUrl}/api/calendar/invoices/`, {
        headers: { 'Authorization': `Token ${token}` }
      }),
    ]);

    if (apptRes.ok) {
      const apptData = await apptRes.json();
      const apptArray = apptData.appointments || (Array.isArray(apptData) ? apptData : apptData.results || []);
      setAppointments(apptArray);
    }

    if (invRes.ok) {
      const invData = await invRes.json();
      const invArray = Array.isArray(invData) ? invData : invData.results || [];
      setInvoices(invArray);
    }
  };

  const waitForBackendPaymentConfirmation = async (invoiceIds, paymentIntentId = null, maxAttempts = 12, delayMs = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const statusRes = await fetch(`${backendBaseUrl}/api/stripe/payment-status/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            invoice_ids: invoiceIds,
            payment_intent_id: paymentIntentId,
          })
        });

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          if (statusData.all_paid) {
            return true;
          }
        }
      } catch (error) {
        console.error('Error checking backend payment status:', error);
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    return false;
  };

  const handlePayInvoice = async (invoiceId) => {
    // Open the unpaid payment flow (overview → Stripe checkout)
    openUnpaidPaymentFlow();
  };

  const handleSuccess = async (setupIntent) => {
    setModalOpen(false);
    setCardSaved(true);

    // Tell the backend to set this payment method as the customer's default
    // so future off-session auto-charges work without patient interaction.
    try {
      await fetch(`${backendBaseUrl}/api/stripe/set-default-payment-method/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          setup_intent_id: setupIntent?.id || null,
        }),
      });
    } catch (e) {
      // Non-critical — the webhook will also set the default as a fallback
      console.warn('Could not set default payment method:', e);
    }
  };

  const handleBillingPortal = async () => {
    try {
      const res = await fetch(`${backendBaseUrl}/api/profile/billing/portal-session/`, { 
        method: 'POST',
        headers: { 'Authorization': `Token ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else {
        alert('Failed to open billing portal');
      }
    } catch (e) {
      console.error('Billing portal error:', e);
      alert('Could not open billing portal');
    }
  };

  const handleAcceptInvoice = async (invoiceId) => {
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/invoices/${invoiceId}/accept/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}` }
      });
      if (res.ok) {
        // Refresh invoices to reflect the accepted status from backend
        const invRes = await fetch(`${backendBaseUrl}/api/calendar/invoices/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        if (invRes.ok) {
          const invData = await invRes.json();
          const invArray = Array.isArray(invData) ? invData : invData.results || [];
          setInvoices(invArray);
        }

        // If auto-pay is enabled and card is saved, charge immediately
        if (paymentPreference === 'auto' && cardSaved) {
          try {
            const chargeRes = await fetch(`${backendBaseUrl}/api/stripe/auto-charge/`, {
              method: 'POST',
              headers: {
                'Authorization': `Token ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ invoice_ids: [invoiceId] }),
            });
            if (chargeRes.ok) {
              const chargeData = await chargeRes.json();
              // Refresh invoices again to reflect paid status
              await refreshAppointmentsAndInvoices();
              alert(`Payment successful! Invoice charged via auto-pay (${chargeData.amount} EUR).`);
              return;
            } else {
              const chargeErr = await chargeRes.json();
              alert(`Invoice accepted but auto-charge failed: ${chargeErr.error || 'Unknown error'}. You can pay manually.`);
              return;
            }
          } catch (chargeError) {
            console.error('Auto-charge error:', chargeError);
            alert('Invoice accepted but auto-charge failed. You can pay manually.');
            return;
          }
        }

        alert('Invoice accepted! It will be processed for auto-pay.');
      } else {
        const data = await res.json();
        alert(`Failed to accept invoice: ${data.error || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Accept error:', e);
      alert('Error accepting invoice');
    }
  };

  const handleChallengeClick = (invoiceId) => {
    setChallengeInvoiceId(invoiceId);
    setChallengeReason('');
    setChallengeDialogOpen(true);
  };

  const handleSubmitChallenge = async () => {
    if (!challengeReason.trim()) {
      alert('Please enter a reason for challenging this invoice');
      return;
    }
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/invoices/${challengeInvoiceId}/challenge/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: challengeReason })
      });
      if (res.ok) {
        alert('Invoice challenge submitted. Your physiotherapist will review shortly.');
        setChallengeDialogOpen(false);
        setChallengeReason('');
        setChallengeInvoiceId(null);
      } else {
        const data = await res.json();
        alert(`Failed to challenge invoice: ${data.error || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Challenge error:', e);
      alert('Error challenging invoice');
    }
  };

  const handleSelectAppointment = (appt) => {
    setSelectedAppointment(appt);
    setAppointmentDialogOpen(true);
  };

  const handleCloseAppointmentDialog = () => {
    setAppointmentDialogOpen(false);
    setSelectedAppointment(null);
    setCancelConfirmDialogOpen(false);
  };

  const isPastAppointment = (appointment) => {
    if (!appointment?.start_datetime) {
      return false;
    }

    return moment(appointment.start_datetime).isBefore(moment());
  };

  const handleCancelAppointment = () => {
    if (isPastAppointment(selectedAppointment)) {
      alert('Past appointments cannot be cancelled.');
      return;
    }

    setCancelConfirmDialogOpen(true);
  };

  const handleConfirmCancelAppointment = async () => {
    if (!selectedAppointment || !token) return;
    if (isPastAppointment(selectedAppointment)) {
      setCancelConfirmDialogOpen(false);
      alert('Past appointments cannot be cancelled.');
      return;
    }
    
    setCancelConfirmDialogOpen(false);
    setCancellingAppointment(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${selectedAppointment.id}/cancel/`, {
        method: 'POST',
        headers: { 
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'cancelled' })
      });
    
      if (response.ok) {
        handleCloseAppointmentDialog();
        // Refresh appointments
        const apptRes = await fetch(`${backendBaseUrl}/api/calendar/appointments/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        if (apptRes.ok) {
          const apptData = await apptRes.json();
          const apptArray = apptData.appointments || (Array.isArray(apptData) ? apptData : apptData.results || []);
          setAppointments(apptArray);
        }
        alert('Appointment cancelled successfully');
      } else {
        let errorMessage = 'Failed to cancel appointment. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          console.error('Failed to parse cancellation error:', parseError);
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      alert('Error cancelling appointment. Please try again.');
    } finally {
      setCancellingAppointment(false);
    }
  };

  const getNextNDays = (n) => {
    const days = [];
    for (let i = 0; i < n; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const dateHasAppointments = (dateStr) => {
    return appointments.some((apt) => {
      const aptDate = moment(apt.start_datetime).format('YYYY-MM-DD');
      return aptDate === dateStr && apt.status !== 'cancelled';
    });
  };

  const handleOpenRescheduleDialog = () => {
    if (!selectedAppointment) return;
    fetchAppointmentInterval();
    fetchConsultationServices();
    fetchStaffMembers();
    const currentDate = new Date(selectedAppointment.start_datetime || selectedAppointment.start);
    const dateStr = moment(currentDate).format('YYYY-MM-DD');
    const timeStr = moment(currentDate).format('HH:00');
    setNewAppointmentDate(dateStr);
    setNewAppointmentTime(timeStr);
    setNewAppointmentType(selectedAppointment.appointment_type || '');
    setSelectedStaffId(selectedAppointment.staff_member || '');
    fetchAvailableTimes(dateStr);
    setRescheduleDialogOpen(true);
  };

  const handleCloseRescheduleDialog = () => {
    setRescheduleDialogOpen(false);
    setNewAppointmentDate('');
    setNewAppointmentTime('');
    setNewAppointmentType('');
    setSelectedStaffId('');
  };

  const handleRescheduleAppointment = async () => {
    if (!selectedAppointment || !newAppointmentDate || !newAppointmentTime || !token) {
      alert('Please select both date and time');
      return;
    }

    setReschedulingAppointment(true);
    try {
      const startDateTime = moment(`${newAppointmentDate} ${newAppointmentTime}`, 'YYYY-MM-DD HH:mm').toDate();
      const endDateTime = new Date(startDateTime);
      endDateTime.setHours(endDateTime.getHours() + 1);

      const payload = {
        start_datetime: startDateTime.toISOString(),
        end_datetime: endDateTime.toISOString()
      };

      if (newAppointmentType) {
        payload.appointment_type = newAppointmentType;
      }

      if (selectedStaffId) {
        payload.staff_member = selectedStaffId;
      }

      if (selectedAppointment.status === 'cancelled') {
        payload.status = 'scheduled';
      }

      const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${selectedAppointment.id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        handleCloseRescheduleDialog();
        handleCloseAppointmentDialog();
        // Refresh appointments
        const apptRes = await fetch(`${backendBaseUrl}/api/calendar/appointments/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        if (apptRes.ok) {
          const apptData = await apptRes.json();
          const apptArray = apptData.appointments || (Array.isArray(apptData) ? apptData : apptData.results || []);
          setAppointments(apptArray);
        }
        alert('Appointment rescheduled successfully');
      } else {
        alert('Failed to reschedule appointment. Please try again.');
      }
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      alert('Error rescheduling appointment. Please try again.');
    } finally {
      setReschedulingAppointment(false);
    }
  };

  const fetchConsultationServices = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/services/bookable/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const services = (Array.isArray(data) ? data : data.services || [])
          .filter((service) => service.is_active && service.is_bookable_online)
          .map((service) => ({
            value: service.id,
            label: service.name,
          }));
        setConsultationServices(services.length > 0 ? services : [
          { value: 'video_consultation', label: 'Video Consultation' },
          { value: 'in_person', label: 'In-Person' },
          { value: 'assessment', label: 'Assessment' },
          { value: 'follow_up', label: 'Follow-up' },
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch consultation services:', error);
      setConsultationServices([
        { value: 'video_consultation', label: 'Video Consultation' },
        { value: 'in_person', label: 'In-Person' },
        { value: 'assessment', label: 'Assessment' },
        { value: 'follow_up', label: 'Follow-up' },
      ]);
    }
  };

  const fetchAppointmentInterval = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/settings/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      // If user is not allowed or has no linked clinic, disable polling and return silently
      if (response.status === 400 || response.status === 404) {
        setShouldPollInterval(false); // Stop polling for this user
        return appointmentInterval;
      }
      
      if (response.ok) {
        const data = await response.json();
        const newInterval = data.appointment_interval || 30;
        
        // If interval changed and times have been fetched, clear them so they'll be re-fetched
        if (appointmentInterval !== null && appointmentInterval !== newInterval && availableTimes.length > 0) {
          console.log(`[PaymentSubscription] Appointment interval changed from ${appointmentInterval} to ${newInterval} - clearing cached times`);
          setAvailableTimes([]);
        }
        
        setAppointmentInterval(newInterval);
        return newInterval;
      }
    } catch (error) {
      // Silently handle network errors during polling
      // This can happen if user navigates away or has intermittent connection
    }
    return appointmentInterval;
  };

  const fetchAvailableTimes = async (dateStr) => {
    setFetchingTimes(true);
    try {
      // Add timestamp to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await fetch(`${backendBaseUrl}/api/calendar/available-times/?date=${dateStr}&t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableTimes(data.available_times || []);
      } else {
        setAvailableTimes([]);
      }
    } catch (error) {
      console.error('Failed to fetch available times:', error);
      setAvailableTimes([]);
    } finally {
      setTimeout(() => setFetchingTimes(false), 1000);
    }
  };

  const fetchStaffMembers = async () => {
    const normalizeStaff = (staffList = []) => {
      return staffList
        .map((staff) => {
          const id = staff.user_id || staff.id || staff.staff_id || staff.staff_member;
          if (!id) return null;
          const fullName = `${staff.first_name || ''} ${staff.last_name || ''}`.trim();
          return {
            ...staff,
            id: String(id),
            user_id: String(id),
            name: staff.name || staff.staff_member_name || fullName || staff.email || 'Staff Member',
          };
        })
        .filter(Boolean);
    };

    const uniqueById = (staffList = []) => {
      return staffList.filter(
        (staff, index, arr) => arr.findIndex((item) => String(item.user_id) === String(staff.user_id)) === index
      );
    };

    try {
      let resolvedStaff = [];

      const response = await fetch(`${backendBaseUrl}/profile/my_careprovider_staff/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        resolvedStaff = normalizeStaff(data.staff || data.results || []);
      }

      if (resolvedStaff.length === 0) {
        const recipientResponse = await fetch(`${backendBaseUrl}/profile/my_message_recipient/`, {
          method: 'GET',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (recipientResponse.ok) {
          const recipientData = await recipientResponse.json();
          resolvedStaff = normalizeStaff(recipientData?.recipient ? [recipientData.recipient] : []);
        }
      }

      if (resolvedStaff.length === 0) {
        const appointmentDerivedStaff = appointments
          .filter((apt) => apt.staff_member)
          .map((apt) => ({
            staff_member: apt.staff_member,
            staff_member_name: apt.staff_member_name,
          }));
        resolvedStaff = normalizeStaff(appointmentDerivedStaff);
      }

      setStaffMembers(uniqueById(resolvedStaff));
    } catch (error) {
      console.error('Failed to fetch staff members:', error);
      setStaffMembers([]);
    }
  };

  const handleToggleInvoiceSelected = (invoiceId) => {
    setSelectedInvoiceIds((prev) => {
      if (prev.includes(invoiceId)) {
        return prev.filter((id) => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const handleSelectAllInvoices = (checked) => {
    if (checked) {
      const allInvoiceIds = pastAppointments
        .map((appt) => getAppointmentInvoice(appt.id))
        .filter((inv) => isOpenInvoice(inv))
        .map(inv => inv.id);
      setSelectedInvoiceIds(allInvoiceIds);
    } else {
      setSelectedInvoiceIds([]);
    }
  };

  const handleAcceptSelected = async () => {
    setBulkActionsAnchor(null);
    let successCount = 0;
    for (const invoiceId of selectedInvoiceIds) {
      try {
        const res = await fetch(`${backendBaseUrl}/api/calendar/invoices/${invoiceId}/accept/`, {
          method: 'POST',
          headers: { 'Authorization': `Token ${token}` }
        });
        if (res.ok) {
          const newAccepted = new Set(acceptedInvoices);
          newAccepted.add(invoiceId);
          setAcceptedInvoices(newAccepted);
          successCount++;
        }
      } catch (e) {
        console.error('Accept error:', e);
      }
    }
    alert(`${successCount} invoice(s) accepted for auto-pay.`);
    setSelectedInvoiceIds([]);
  };

  const handleChallengeSelected = () => {
    setBulkActionsAnchor(null);
    if (selectedInvoiceIds.length === 1) {
      handleChallengeClick(selectedInvoiceIds[0]);
    } else {
      alert('Please select only one invoice to challenge at a time.');
    }
  };

  const getCourseName = (treatmentId) => {
    if (!treatmentId) return '—';
    const treatment = treatments.find(t => String(t.id) === String(treatmentId));
    return treatment?.name || treatment?.diagnosis?.diagnosis || '—';
  };

  const getAppointmentLabel = (appointmentType) => {
    if (!appointmentType) return 'Appointment';
    const normalized = appointmentType.toLowerCase();
    if (normalized.includes('video')) return 'Video Consultation';
    if (normalized.includes('follow')) return 'Follow-up';
    if (normalized.includes('assessment')) return 'Assessment';
    if (normalized.includes('treatment')) return 'Treatment';
    if (normalized.includes('in_person')) return 'In-Person';
    return appointmentType.replace(/_/g, ' ');
  };

  const getAppointmentStatusChip = (appointment) => {
    const makeChip = (label, backgroundColor, color) => ({ label, style: { backgroundColor, color } });
    const status = appointment.status?.toLowerCase() || '';
    
    if (status === 'cancelled' || status === 'canceled') {
      return makeChip('Cancelled', '#C62828', '#fff');
    }
    if (status === 'completed') {
      return makeChip('Completed', '#2E7D32', '#fff');
    }
    if (status === 'no_show' || status === 'no-show') {
      return makeChip('No Show', '#F57C00', '#fff');
    }
    // Default for scheduled/confirmed appointments
    return makeChip('Scheduled', '#4ee63a', '#ffff');
  };

  const lineMatchesAppointment = (line, appointmentId) => {
    const directId = line?.appointment?.id || line?.appointment || line?.appointment_id;
    return String(directId) === String(appointmentId);
  };

  const reversedInvoiceStatuses = new Set(['accredited', 'refunded', 'cancelled']);

  const isOpenInvoice = (invoice) => {
    const status = String(invoice?.status || '').toLowerCase();
    return Boolean(status) && status !== 'paid' && !reversedInvoiceStatuses.has(status);
  };

  const getAppointmentInvoice = (appointmentId) => {
    const matchingInvoices = invoices.filter((inv) => (
      inv.line_items && inv.line_items.some((line) => lineMatchesAppointment(line, appointmentId))
    ));

    if (matchingInvoices.length === 0) {
      return null;
    }

    return matchingInvoices.find((inv) => String(inv.status || '').toLowerCase() === 'paid')
      || matchingInvoices.find((inv) => !reversedInvoiceStatuses.has(String(inv.status || '').toLowerCase()))
      || matchingInvoices[0];
  };

  const getAppointmentByInvoice = (invoiceId) => (
    appointments.find((appt) => {
      const linkedInvoice = getAppointmentInvoice(appt.id);
      return linkedInvoice && String(linkedInvoice.id) === String(invoiceId);
    }) || null
  );

  const isAppointmentPaid = (appointment, invoice) => {
    if (invoice) {
      return invoice.status === 'paid';
    }
    if (appointment.billing_status === 'invoiced' || appointment.billing_status === 'billed') {
      return false;
    }
    return appointment.payment_status === 'paid';
  };

  const isBilledUnpaidAppointment = (appointment, invoice) => {
    if (appointment.status === 'cancelled' || appointment.status === 'canceled') {
      return false;
    }

    if (invoice) {
      const settledStatuses = new Set(['paid', 'cancelled', 'accredited', 'inkasso']);
      return !settledStatuses.has(invoice.status);
    }

    const billedStatuses = new Set(['invoiced', 'billed']);
    return billedStatuses.has(appointment.billing_status) && appointment.payment_status !== 'paid';
  };

  const isBilledPaidAppointment = (appointment, invoice) => {
    if (appointment.status === 'cancelled' || appointment.status === 'canceled') {
      return false;
    }

    if (invoice) {
      return invoice.status === 'paid';
    }

    return appointment.payment_status === 'paid';
  };

  const openUnpaidPaymentFlow = () => {
    setUnpaidFlowStep('overview');
    setUnpaidFlowOpen(true);
  };

  const handleCloseUnpaidFlow = () => {
    if (processingUnpaidFlow || paymentProcessing) return;
    setUnpaidFlowOpen(false);
    setUnpaidFlowStep('overview');
    setPaymentConfirmation(null);
    setPaymentIntentSecret(null);
  };

  const handleProceedToStripeStep = async () => {
    if (unpaidInvoiceIdsForFlow.length === 0) {
      alert('No unpaid invoices to pay');
      return;
    }
    
    setPaymentProcessing(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/stripe/payment-intent/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: unpaidAppointmentsTotal,
          invoice_ids: unpaidInvoiceIdsForFlow
        })
      });
      
      if (!response.ok) {
        const rawBody = await response.text();
        let message = `Failed to create payment intent (${response.status})`;
        try {
          const parsed = JSON.parse(rawBody);
          message = parsed.error || message;
        } catch (parseError) {
          console.error('Non-JSON error response while creating payment intent:', rawBody);
        }

        alert(`Error: ${message}`);
        setPaymentProcessing(false);
        return;
      }
      
      const data = await response.json();
      setPaymentIntentSecret(data.client_secret);
      setUnpaidFlowStep('checkout');
    } catch (error) {
      console.error('Error creating payment intent:', error);
      alert('Error creating payment intent. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleStripeCheckoutSubmit = async (result) => {
    if (result.success) {
      try {
        setPaymentProcessing(true);
        const paymentIntentId = result.paymentIntent?.id || null;
        const confirmedInBackend = await waitForBackendPaymentConfirmation(unpaidInvoiceIdsForFlow, paymentIntentId);
        await refreshAppointmentsAndInvoices();

        if (!confirmedInBackend) {
          alert('Stripe payment was accepted, but backend confirmation is still pending. Please refresh in a few moments.');
          setUnpaidFlowStep('overview');
          return;
        }

        setPaymentConfirmation({
          successCount: unpaidInvoiceIdsForFlow.length,
          failedCount: 0,
          timestamp: new Date().toISOString(),
          invoiceIds: unpaidInvoiceIdsForFlow
        });
        setUnpaidFlowStep('confirmation');
      } catch (error) {
        console.error('Error refreshing invoices:', error);
        alert('Payment successful but failed to update. Please reload the page.');
      } finally {
        setPaymentProcessing(false);
      }
    } else {
      alert(`Payment status: ${result.paymentIntent?.status || 'unknown'}`);
    }
  };

  // Fetch invoice details by ID
  const handleViewInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/invoices/${invoiceId}/`, {
        method: 'GET',
        headers: { 'Authorization': `Token ${token}` }
      });
      if (!response.ok) {
        console.error('Failed to fetch invoice');
        return;
      }
      const data = await response.json();
      setSelectedInvoiceForPreview(data);
      setInvoicePreviewOpen(true);
    } catch (error) {
      console.error('Error fetching invoice:', error);
    }
  };

  // Generate PDF from invoice for viewing
  const handleDownloadInvoicePDF = () => {
    if (!selectedInvoiceForPreview) return;
    const element = document.getElementById('invoice-printable-content');
    if (!element) return;
    const opt = {
      margin: 10,
      filename: `Invoice_${selectedInvoiceForPreview.invoice_number}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    };
    html2pdf().set(opt).from(element).save();
  };

  const handleOpenInvoiceActions = (event, invoice) => {
    setInvoiceActionsAnchorEl(event.currentTarget);
    setInvoiceActionsInvoice(invoice);
  };

  const handleCloseInvoiceActions = () => {
    setInvoiceActionsAnchorEl(null);
    setInvoiceActionsInvoice(null);
  };

  // Generate paid receipt PDF from payment confirmation
  const handleGenerateReceiptPDF = () => {
    if (!paymentConfirmation) return;
    
    const receiptElement = document.createElement('div');
    const paymentDate = new Date(paymentConfirmation.timestamp);
    
    receiptElement.innerHTML = `
      <div style="font-family: Arial, sans-serif; background-color: #FFF; padding: 40px;">
        {/* Header Section */}
        <div style="margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #002D55;">
          <div style="font-size: 24px; font-weight: bold; color: #002D55; margin-bottom: 5px;">VIRTUS PHYSIO</div>
          <div style="font-size: 12px; color: #666; margin-bottom: 15px;">
            Physiotherapy & Rehabilitation Center<br/>
            Address: Your Clinic Address<br/>
            Phone: +1 (000) 000-0000 | Email: clinic@virtusphysio.com
          </div>
        </div>

        {/* Receipt Title */}
        <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
          <div>
            <div style="font-size: 28px; font-weight: bold; color: #002D55;">PAYMENT RECEIPT</div>
            <div style="font-size: 14px; color: #4caf50; font-weight: bold; margin-top: 8px; text-transform: uppercase;">Γ£ô PAYMENT RECEIVED</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 12px; color: #666; margin-bottom: 5px;"><b>Payment Date:</b> ${paymentDate.toLocaleDateString()} ${paymentDate.toLocaleTimeString()}</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 5px;"><b>Invoices Paid:</b> ${paymentConfirmation.invoiceIds.length}</div>
            <div style="font-size: 12px; color: #666;"><b>Amount:</b> EUR ${unpaidAppointmentsTotal.toFixed(2)}</div>
          </div>
        </div>

        {/* Payment Summary */}
        <div style="margin-bottom: 30px; background-color: #d4edda; padding: 20px; border-radius: 4px; border: 2px solid #4caf50;">
          <div style="font-size: 12px; font-weight: bold; color: #155724; margin-bottom: 15px; text-transform: uppercase;">Payment Confirmation Summary</div>
          
          <div style="margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #c3e6cb;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 12px;">
              <span>Total Amount Paid:</span>
              <span style="font-weight: bold;">EUR ${unpaidAppointmentsTotal.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 12px;">
              <span>Payment Method:</span>
              <span>Stripe Secure Payment</span>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="font-size: 11px; font-weight: bold; color: #155724; margin-bottom: 8px;">Invoices Included:</div>
            <div style="font-size: 11px; color: #155724;">
              ${paymentConfirmation.invoiceIds.map((id, idx) => `<div style="margin-bottom: 4px;">ΓÇó Invoice ID: ${id}</div>`).join('')}
            </div>
          </div>

          <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #c3e6cb;">
            <div style="display: flex; justify-content: space-between; font-size: 12px; font-weight: bold;">
              <span>Successful Transactions:</span>
              <span style="color: #155724;">${paymentConfirmation.successCount}</span>
            </div>
            ${paymentConfirmation.failedCount > 0 ? `
            <div style="display: flex; justify-content: space-between; font-size: 12px; font-weight: bold; color: #d9534f; margin-top: 8px;">
              <span>Failed Transactions:</span>
              <span>${paymentConfirmation.failedCount}</span>
            </div>
            ` : ''}
          </div>
        </div>

        {/* Status Banner */}
        <div style="margin-bottom: 30px; padding: 16px; background-color: #f0f7ff; border-left: 4px solid #2196f3; border-radius: 2px;">
          <div style="font-size: 12px; color: #0056b3; font-weight: 600;">
            Γ£ô Your payment has been received and processed successfully. Your invoice(s) have been marked as PAID.
          </div>
        </div>

        {/* Footer */}
        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #E0E0E0; font-size: 10px; color: #666;">
          <div style="font-size: 10px; color: #666; margin-bottom: 8px;"><b>Payment Details:</b></div>
          <div style="font-size: 10px; color: #666; margin-bottom: 8px;">ΓÇó Payment processed securely via Stripe</div>
          <div style="font-size: 10px; color: #666; margin-bottom: 8px;">ΓÇó All invoice(s) marked as PAID in the system</div>
          <div style="font-size: 10px; color: #666;">ΓÇó Please retain this receipt for your records. For questions, contact us at clinic@virtusphysio.com</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(receiptElement);
    const opt = {
      margin: 10,
      filename: `Receipt_${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}-${String(paymentDate.getDate()).padStart(2, '0')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    };
    html2pdf().set(opt).from(receiptElement).save();
    document.body.removeChild(receiptElement);
  };

  const handleDownloadReceipt = () => {
    handleGenerateReceiptPDF();
  };

  const handlePayAllDueInvoices = async () => {
    if (unpaidInvoiceIdsForFlow.length === 0) {
      alert('No unpaid billed invoices found.');
      return;
    }

    setProcessingUnpaidFlow(true);
    try {
      // Stripe payment is handled by StripePayment component elsewhere
      // Payment success callback will update invoices via refreshAppointmentsAndInvoices
      // Just show confirmation after Stripe processing completes
      
      await refreshAppointmentsAndInvoices();
      
      // Set confirmation data and show confirmation screen
      setPaymentConfirmation({
        successCount: unpaidInvoiceIdsForFlow.length,
        failedCount: 0,
        timestamp: new Date().toISOString(),
        invoiceIds: unpaidInvoiceIdsForFlow
      });
      setUnpaidFlowStep('confirmation');
    } catch (error) {
      console.error('Payment processing error:', error);
      alert('Error processing payment. Please try again.');
    } finally {
      setProcessingUnpaidFlow(false);
    }
  };

  const isInvoiceActionable = (invoice) => {
    if (!invoice) return false;
    // Invoice is actionable if it's not already paid/cancelled and not yet accepted
    const notYetActed = new Set(['draft', 'sent', 'reopened', 'viewed', 'overdue', 'partially_paid', 'reminder_1', 'reminder_2', 'reminder_3']);
    return notYetActed.has(invoice.status);
  };

  const hasAcceptedOrChallenged = (invoice) => {
    if (!invoice) return false;
    return ['accepted', 'challenged'].includes(invoice.status);
  };

  const getAppointmentBillingChip = (appointment, invoice) => {
    const makeChip = (label, backgroundColor, color) => ({ label, style: { backgroundColor, color } });

    if (invoice) {
      switch (invoice.status) {
        case 'paid':
          return makeChip('Paid', '#E8F5E9', '#2E7D32');
        case 'accredited':
          return makeChip('Accredited', '#E3F2FD', '#1565C0');
        case 'inkasso':
          return makeChip('Inkasso', '#FFEBEE', '#C62828');
        case 'reminder_1':
          return makeChip('Billed (1st reminder)', '#FFF3E0', '#F57C00');
        case 'reminder_2':
          return makeChip('Billed (2nd reminder)', '#FFE0B2', '#EF6C00');
        case 'reminder_3':
          return makeChip('Billed (3rd reminder)', '#FFCCBC', '#E64A19');
        case 'draft':
        case 'sent':
        case 'reopened':
        case 'viewed':
        case 'accepted':
        case 'challenged':
        case 'overdue':
        case 'partially_paid':
          return makeChip('Billed (payment due)', '#FFF3E0', '#F57C00');
        default:
          return makeChip('Billed (payment due)', '#FFF3E0', '#F57C00');
      }
    }

    if (appointment.payment_status === 'paid') {
      return makeChip('Paid', '#E8F5E9', '#2E7D32');
    }
    if (appointment.billing_status === 'unbilled' || appointment.billing_status === 'bookable') {
      return makeChip('Unbilled', '#EEEEEE', '#666');
    }
    if (appointment.billing_status === 'invoiced') {
      return makeChip('Billed (payment due)', '#FFF3E0', '#F57C00');
    }

    return makeChip('Unbilled', '#EEEEEE', '#666');
  };

  const getAppointmentPayer = (appointment) => {
    const payerValue = String(
      appointment?.payer || appointment?.billing_payer || appointment?.payment_responsibility || ''
    ).toLowerCase();
    const insurancePaid = appointment?.insurance_paid === true;
    const insurancePayer = insurancePaid || payerValue === 'insurance' || payerValue === 'insurer';

    if (insurancePayer) {
      return {
        label: 'Insurance',
        style: {
          backgroundColor: '#E3F2FD',
          color: '#1565C0',
          fontWeight: 600,
          fontSize: 11,
        },
      };
    }

    return {
      label: 'Patient',
      style: {
        backgroundColor: '#9E9E9E',
        color: '#fff',
        fontWeight: 600,
        fontSize: 11,
      },
    };
  };

  // Calculate stats
  const unpaidInvoices = invoices.filter((inv) => isOpenInvoice(inv));
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');

  // Separate upcoming and past appointments first
  const now = new Date();
  const upcomingAppointments = appointments.filter((appt) => new Date(appt.start_datetime) >= now);
  const pastAppointments = appointments.filter((appt) => new Date(appt.start_datetime) < now);
  
  // Count only PAST appointments for billing stats (excluding future scheduled ones)
  const paidCount = pastAppointments.filter((appt) => {
    const invoice = getAppointmentInvoice(appt.id);
    return isBilledPaidAppointment(appt, invoice);
  }).length;
  const unpaidCount = pastAppointments.filter((appt) => {
    const invoice = getAppointmentInvoice(appt.id);
    return isBilledUnpaidAppointment(appt, invoice);
  }).length;

  const unpaidBilledAppointments = pastAppointments.filter((appt) => {
    const invoice = getAppointmentInvoice(appt.id);
    return isBilledUnpaidAppointment(appt, invoice);
  });

  const unpaidBilledByMonth = groupAppointmentsByMonth(unpaidBilledAppointments);

  const unpaidAppointmentsTotal = unpaidBilledAppointments.reduce((sum, appt) => {
    return sum + (Number(appt.booked_price) || 0);
  }, 0);

  const unpaidInvoiceIdsForFlow = Array.from(new Set(
    unpaidInvoices
      .map((inv) => inv.id)
      .filter(Boolean)
  ));

  //console.log('Payment Stats:', {
  //  totalAppointments: appointments.length,
  //  upcomingAppointments: upcomingAppointments.length,
  //  pastAppointments: pastAppointments.length,
  //  paidCount,
  //  unpaidCount
  //});

  // Group appointments by month
  function groupAppointmentsByMonth(items) {
    const groups = {};
    items.forEach((item) => {
      const date = new Date(item.start_datetime);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!groups[key]) {
        groups[key] = { key, monthName, items: [] };
      }
      groups[key].items.push(item);
    });
    return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
  }

  function groupInvoicesByMonth(items) {
    const groups = {};
    items.forEach((item) => {
      const sourceDate = item.paid_date || item.payment_received_date || item.invoice_date || item.created;
      const date = new Date(sourceDate);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!groups[key]) {
        groups[key] = { key, monthName, items: [] };
      }
      groups[key].items.push(item);
    });
    return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
  }

  const pastAppointmentsByMonth = groupAppointmentsByMonth(pastAppointments);
  const paidInvoicesByMonth = groupInvoicesByMonth(paidInvoices);

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const selectedAppointmentIsPast = isPastAppointment(selectedAppointment);

  return (
    <form
      autoComplete="off"
      noValidate
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeaderRoot}
          title={<span className={classes.cardHeaderTitle}>Payment Overview</span>}
          style={{ marginBottom: 0 }}
        />
        <CardContent className={classes.cardContent}>


          {/* ── Payment Method ── */}
          <Typography className={classes.cardHeaderSubTitle} style={{ fontSize: 'clamp(14px, 1.15vw, 18px)', marginBottom: 16 }}>
            Payment Method
          </Typography>
          {/* ── Intro text ── */}
          <Typography className={classes.bodyText}>
            Payments are securely handled by Stripe, including credit/debit cards, Apple Pay, and MobilePay.
          </Typography>
          <Chip
            label={cardSaved ? 'Card on file' : 'No card on file'}
            className={cardSaved ? classes.statusChipActive : classes.statusChip}
            size="small"
          />

          <Divider style={{ marginTop: 24, marginBottom: 4 }} />
          <List>
            <ListItem>
              <ListItemIcon>
                <CreditCardIcon style={{ color: "#002D55", fontSize: 'clamp(18px, 1.4vw, 22px)' }} />
              </ListItemIcon>
              <ListItemText
                primary={cardSaved ? 'Payment method saved' : 'Add a payment method'}
                secondary="Cards are stored securely in Stripe for future invoices."
                primaryTypographyProps={{ style: { color: "#002D55", fontWeight: 600, fontSize: 'clamp(12px, 0.85vw, 14px)' } }}
                secondaryTypographyProps={{ style: { color: "#666666", fontSize: 'clamp(11px, 0.82vw, 13px)' } }}
              />
            </ListItem>
          </List>
          <Button
            className={classes.Button}
            variant="contained"
            fullWidth
            style={{ marginTop: 8 }}
            onClick={() => setModalOpen(true)}
          >
            {cardSaved ? 'Update Payment Method' : 'Add Payment Method'}
          </Button>

          <Divider style={{ marginTop: 24, marginBottom: 24 }} />

          {/* ── Payment Preference ── */}
          <Typography className={classes.cardHeaderSubTitle} style={{ fontSize: 'clamp(14px, 1.15vw, 18px)', marginBottom: 6 }}>
            Payment Preference
          </Typography>
          {loadingPref ? (
            <CircularProgress size={20} />
          ) : (
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={paymentPreference}
                onChange={(e) => handlePreferenceChange(e.target.value)}
                style={{ flexWrap: 'nowrap', alignItems: 'center', gap: 8 }}
              >
                <FormControlLabel
                  value="auto"
                  control={<Radio style={{ color: (paymentPreference === 'auto' || highlightAutoPayRequest) ? '#fb741a' : '#002D55' }} />}
                  style={highlightAutoPayRequest ? {
                    backgroundColor: '#FFF8E1',
                    border: '1px solid #FFB74D',
                    borderRadius: 8,
                    padding: '2px 8px',
                    marginRight: 0,
                    flexShrink: 0,
                    alignItems: 'center',
                  } : { flexShrink: 0, alignItems: 'center' }}
                  label={<span style={{ fontSize: 'clamp(12px, 1vw, 15px)', color: '#666666' }}>Auto-pay (charge saved card when invoices are issued)</span>}
                />
                <FormControlLabel
                  value="invoice"
                  control={<Radio style={{ color: paymentPreference === 'invoice' ? '#fb741a' : '#002D55' }} />}
                  style={{ flexShrink: 0, alignItems: 'center' }}
                  label={<span style={{ fontSize: 'clamp(12px, 1vw, 15px)', color: '#666666' }}>Manual payment (receive invoices)</span>}
                />
                {highlightAutoPayRequest && (
                  <Typography style={{ fontSize: 12, color: '#E65100', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    ⚠ Auto-pay requested
                  </Typography>
                )}
              </RadioGroup>
            </FormControl>
          )}

          <Divider style={{ marginTop: 24, marginBottom: 24 }} />

          {/* ── Appointment Billing Status ── */}
          <Typography className={classes.cardHeaderSubTitle} style={{ fontSize: 'clamp(14px, 1.15vw, 18px)', marginBottom: 16 }}>
            Appointment Billing History
          </Typography>
          {loadingData ? (
            <Box display="flex" justifyContent="center" padding={3}>
              <CircularProgress />
            </Box>
          ) : appointments.length === 0 ? (
            <Typography style={{ color: '#999', fontStyle: 'italic', padding: '12px 0' }}>
              No appointments found.
            </Typography>
          ) : (
            <>
              <Box style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <Box style={{ flex: 1, textAlign: 'center', padding: '12px', backgroundColor: '#E8F5E9', borderRadius: 8 }}>
                  <Typography style={{ fontWeight: 600, color: '#2E7D32', fontSize: 'clamp(12px, 0.85vw, 14px)' }}>Paid</Typography>
                  <Typography style={{ fontSize: 'clamp(15px, 1.3vw, 20px)', fontWeight: 'bold', color: '#2E7D32' }}>{paidCount}</Typography>
                </Box>
                <Box
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '12px',
                    backgroundColor: '#FFF3E0',
                    borderRadius: 8,
                    cursor: unpaidCount > 0 ? 'pointer' : 'default',
                    border: unpaidCount > 0 ? '1px solid #FFE0B2' : 'none'
                  }}
                  onClick={() => {
                    if (unpaidCount > 0) {
                      openUnpaidPaymentFlow();
                    }
                  }}
                  title={unpaidCount > 0 ? 'View unpaid billed appointments and proceed to Stripe payment' : ''}
                >
                  <Typography style={{ fontWeight: 400, color: '#666666', fontSize: 'clamp(11px, 0.85vw, 13px)' }}><span style={{ fontSize: 'clamp(15px, 1.3vw, 20px)', fontWeight: 'bold', color: '#F57C00' }}>Unpaid</span> (Click to view & proceed to payment)</Typography>
                  <Typography style={{ fontSize: 'clamp(15px, 1.3vw, 20px)', fontWeight: 'bold', color: '#F57C00' }}>{unpaidCount}</Typography>
                </Box>
              </Box>

              {/* Upcoming Appointments */}
              {upcomingAppointments.length > 0 && (
                <Box style={{ marginBottom: 16 }}>
                  <Typography style={{ fontWeight: 600, color: '#002D55', marginBottom: 8 }}>
                    Upcoming Appointments ({upcomingAppointments.length})
                  </Typography>
                  <TableContainer component={Card} style={{ backgroundColor: "#FFFFFF", borderRadius: 8 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ backgroundColor: "#F0F8FF" }}>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Date & Time</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Course</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Service</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Staff</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Payer</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Amount</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Invoice</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }} align="center">Status</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }} align="center">Payment</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {upcomingAppointments.map((appt) => {
                          const primaryInvoice = getAppointmentInvoice(appt.id);
                          const billingChip = getAppointmentBillingChip(appt, primaryInvoice);
                          const statusChip = getAppointmentStatusChip(appt);
                          return (
                            <TableRow
                              key={appt.id}
                              onClick={() => handleSelectAppointment(appt)}
                              style={{
                                borderLeft: '3px solid #2196F3',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#f5f5f5' }
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <TableCell>
                                <Typography style={{ fontWeight: 600, fontSize: 'clamp(11px, 0.9vw, 14px)' }}>
                                  {formatDate(appt.start_datetime)}
                                </Typography>
                                <Typography style={{ fontSize: 'clamp(9px, 0.78vw, 12px)', color: '#666' }}>
                                  {formatTime(appt.start_datetime)}
                                </Typography>
                              </TableCell>
                              <TableCell style={{ fontSize: 'clamp(10px, 0.82vw, 13px)' }}>{getCourseName(appt.treatment)}</TableCell>
                              <TableCell style={{ fontSize: 'clamp(10px, 0.82vw, 13px)' }}>{appt.title || getAppointmentLabel(appt.appointment_type)}</TableCell>
                              <TableCell style={{ fontSize: 'clamp(10px, 0.82vw, 13px)' }}>{appt.staff_member_name || '—'}</TableCell>
                              <TableCell style={{ fontSize: 'clamp(10px, 0.82vw, 13px)' }}>
                                {appt.payer_type === 'insurance' ? (
                                  <Box>
                                    <Typography style={{ fontSize: 'clamp(9px, 0.78vw, 12px)', fontWeight: 600, color: '#fb741a' }}>
                                      Insurance
                                    </Typography>
                                    <Typography style={{ fontSize: 'clamp(9px, 0.72vw, 11px)', color: '#666666' }}>
                                      {appt.payer_referral_letter ? appt.payer_referral_letter : '(No referral)'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography style={{ fontSize: 'clamp(9px, 0.78vw, 12px)', color: '#666666' }}>
                                    Patient
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell style={{ fontSize: 13 }}>
                                <Box>
                                  {appt.payer_type === 'insurance' && (appt.insurance_covered_amount !== null && appt.insurance_covered_amount !== undefined) ? (
                                    <>
                                      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#002D55' }}>
                                        {appt.booked_currency || 'EUR'} {Number(appt.insurance_covered_amount || 0).toFixed(2)}
                                      </Typography>
                                      {appt.patient_out_of_pocket_amount && Number(appt.patient_out_of_pocket_amount) > 0 && (
                                        <Typography style={{ fontSize: 11, color: '#666666', marginTop: 2 }}>
                                          + {appt.booked_currency || 'EUR'} {Number(appt.patient_out_of_pocket_amount).toFixed(2)} (You)
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    <Typography style={{ fontSize: 12, color: '#002D55' }}>
                                      {appt.booked_currency || 'EUR'} {appt.booked_price || '0.00'}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {primaryInvoice ? (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewInvoice(primaryInvoice.id);
                                    }}
                                    style={{cursor: 'pointer', color: '#002D55', fontWeight: 600, textDecoration: 'underline', fontSize: 12}}
                                  >
                                    #{primaryInvoice.invoice_number}
                                  </span>
                                ) : (
                                  <Typography style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
                                    Pending
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={statusChip.label}
                                  style={statusChip.style}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={billingChip.label}
                                  style={billingChip.style}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Past Appointments - Grouped by Month */}
              {pastAppointments.length > 0 && (
                <Box>
                  <Typography style={{ fontSize: 'clamp(14px, 1.15vw, 18px)',fontWeight: 600, color: '#002D55', marginBottom: 8 }}>
                    Past Appointments ({pastAppointments.length})
                  </Typography>
                  {pastAppointmentsByMonth.map((monthGroup) => (
                    <Accordion key={monthGroup.key} style={{ marginBottom: 8, borderRadius: 8, overflow: 'hidden' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f5f5f5' }}>
                        <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(12px, 0.9vw, 15px)' }}>
                          {monthGroup.monthName} ({monthGroup.items.length} appointments)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails style={{ padding: 0 }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow style={{ backgroundColor: "#F0F8FF" }}>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Date & Time</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Course</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Service</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Staff</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Payer</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Amount</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Invoice</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }} align="center">Status</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }} align="center">Payment</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="center">
                                  <Checkbox
                                    checked={selectedInvoiceIds.length === pastAppointments.filter(appt => {
                                      const inv = getAppointmentInvoice(appt.id);
                                      return inv && inv.status !== 'paid';
                                    }).length && selectedInvoiceIds.length > 0}
                                    indeterminate={selectedInvoiceIds.length > 0 && selectedInvoiceIds.length < pastAppointments.filter(appt => {
                                      const inv = getAppointmentInvoice(appt.id);
                                      return inv && inv.status !== 'paid';
                                    }).length}
                                    onChange={(e) => handleSelectAllInvoices(e.target.checked)}
                                    color="primary"
                                    size="small"
                                    title="Select All Unpaid"
                                  />
                                </TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="center">
                                  <Button
                                    size="small"
                                    onClick={(e) => setBulkActionsAnchor(e.currentTarget)}
                                    disabled={selectedInvoiceIds.length === 0}
                                    style={{ 
                                      fontSize: 11, 
                                      textTransform: 'none',
                                      color: selectedInvoiceIds.length === 0 ? '#999' : '#002D55'
                                    }}
                                  >
                                    Actions ({selectedInvoiceIds.length})
                                  </Button>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {monthGroup.items.map((appt) => {
                                const primaryInvoice = getAppointmentInvoice(appt.id);
                                const appointmentIsPaid = isAppointmentPaid(appt, primaryInvoice);
                                const statusChip = getAppointmentStatusChip(appt);
                                return (
                                  <TableRow
                                    key={appt.id}
                                    style={{
                                      borderLeft: appointmentIsPaid ? '3px solid #4CAF50' : '3px solid #FFC107'
                                    }}
                                  >
                                    <TableCell>
                                      <Typography style={{ fontWeight: 600, fontSize: 'clamp(11px, 0.9vw, 14px)' }}>
                                        {formatDate(appt.start_datetime)}
                                      </Typography>
                                      <Typography style={{ fontSize: 'clamp(9px, 0.78vw, 12px)', color: '#666' }}>
                                        {formatTime(appt.start_datetime)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell style={{ fontSize: 'clamp(10px, 0.82vw, 13px)' }}>{getCourseName(appt.treatment)}</TableCell>
                                    <TableCell style={{ fontSize: 'clamp(10px, 0.82vw, 13px)' }}>{appt.title || getAppointmentLabel(appt.appointment_type)}</TableCell>
                                    <TableCell style={{ fontSize: 'clamp(10px, 0.82vw, 13px)' }}>{appt.staff_member_name || '—'}</TableCell>
                                    <TableCell style={{ fontSize: 'clamp(10px, 0.82vw, 13px)' }}>
                                      {appt.payer_type === 'insurance' ? (
                                        <Box>
                                          <Typography style={{ fontSize: 'clamp(9px, 0.78vw, 12px)', fontWeight: 600, color: '#fb741a' }}>
                                            Insurance
                                          </Typography>
                                          <Typography style={{ fontSize: 'clamp(9px, 0.72vw, 11px)', color: '#666666' }}>
                                            {appt.payer_referral_letter ? appt.payer_referral_letter : '(No referral)'}
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <Typography style={{ fontSize: 12, color: '#666666' }}>
                                          Patient
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 13 }}>
                                      <Box>
                                        {appt.payer_type === 'insurance' && (appt.insurance_covered_amount !== null && appt.insurance_covered_amount !== undefined) ? (
                                          <>
                                            <Typography style={{ fontSize: 12, fontWeight: 600, color: '#002D55' }}>
                                              {appt.booked_currency || 'EUR'} {Number(appt.insurance_covered_amount || 0).toFixed(2)}
                                            </Typography>
                                            {appt.patient_out_of_pocket_amount && Number(appt.patient_out_of_pocket_amount) > 0 && (
                                              <Typography style={{ fontSize: 11, color: '#666666', marginTop: 2 }}>
                                                + {appt.booked_currency || 'EUR'} {Number(appt.patient_out_of_pocket_amount).toFixed(2)} (You)
                                              </Typography>
                                            )}
                                          </>
                                        ) : (
                                          <Typography style={{ fontSize: 12, color: '#002D55' }}>
                                            {appt.booked_currency || 'EUR'} {appt.booked_price || '0.00'}
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      {primaryInvoice ? (
                                        <span
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewInvoice(primaryInvoice.id);
                                          }}
                                          style={{cursor: 'pointer', color: '#002D55', fontWeight: 600, textDecoration: 'underline', fontSize: 12}}
                                        >
                                          #{primaryInvoice.invoice_number}
                                        </span>
                                      ) : (
                                        <Typography style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
                                          Not invoiced
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        size="small"
                                        label={statusChip.label}
                                        style={statusChip.style}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      {(() => {
                                        const chip = getAppointmentBillingChip(appt, primaryInvoice);
                                        return (
                                          <Chip
                                            size="small"
                                            label={chip.label}
                                            style={chip.style}
                                          />
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                      {primaryInvoice && isInvoiceActionable(primaryInvoice) ? (
                                        <Checkbox
                                          checked={selectedInvoiceIds.includes(primaryInvoice.id)}
                                          onChange={() => handleToggleInvoiceSelected(primaryInvoice.id)}
                                          color="primary"
                                          size="small"
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      ) : null}
                                    </TableCell>
                                    <TableCell align="center">
                                      {primaryInvoice && isInvoiceActionable(primaryInvoice) && !hasAcceptedOrChallenged(primaryInvoice) && (
                                        <Box style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            style={{ backgroundColor: '#4CAF50', color: '#fff', fontSize: 10, padding: '2px 8px', textTransform: 'none' }}
                                            onClick={() => handleAcceptInvoice(primaryInvoice.id)}
                                          >
                                            Accept
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            style={{ borderColor: '#F57C00', color: '#F57C00', fontSize: 10, padding: '2px 8px', textTransform: 'none' }}
                                            onClick={() => handleChallengeClick(primaryInvoice.id)}
                                          >
                                            Challenge
                                          </Button>
                                        </Box>
                                      )}
                                      {primaryInvoice && hasAcceptedOrChallenged(primaryInvoice) && (
                                        <Typography style={{ fontSize: 11, color: '#F57C00', fontStyle: 'italic' }}>
                                          {primaryInvoice.status === 'accepted' ? 'Accepted' : 'Challenged'}
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </>
          )}

          <Divider style={{ marginTop: 24, marginBottom: 24 }} />

          {/* ── Unpaid Invoices ── */}
          <Typography className={classes.cardHeaderSubTitle} style={{ fontSize: 'clamp(14px, 1.15vw, 18px)', marginBottom: 16 }}>
            Unpaid Invoices
          </Typography>
          {loadingData ? (
            <CircularProgress size={20} />
          ) : unpaidInvoices.length === 0 ? (
            <Typography style={{ color: '#999', fontStyle: 'italic' }}>
              No unpaid invoices.
            </Typography>
          ) : (
            <Box>
              {unpaidInvoices.map((invoice) => (
                <Box key={invoice.id} style={{ marginBottom: 16, border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ backgroundColor: "#F0F8FF" }}>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', width: '30%', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Details</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', width: '20%', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Amount</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', width: '20%', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Due Date</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55', width: '30%', fontSize: 'clamp(10px, 0.82vw, 13px)' }} align="center">Payment</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Invoice Row */}
                        <TableRow style={{ borderLeft: '3px solid #FFC107', backgroundColor: '#FAFAFA' }}>
                          <TableCell colSpan={4} style={{ padding: '8px 12px' }}>
                            <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>
                              Invoice: #{invoice.invoice_number}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        
                        {/* Details Row */}
                        <TableRow style={{ borderLeft: '3px solid #FFC107' }}>
                          <TableCell style={{ paddingTop: 12, paddingBottom: 4 }}>
                            <Typography style={{ fontSize: 12, color: '#666' }}>Amount & Due</Typography>
                          </TableCell>
                          <TableCell style={{ paddingTop: 12, paddingBottom: 4 }}>
                            <Typography style={{ fontWeight: 600, color: '#F57C00', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>
                              {invoice.currency || 'EUR'} {invoice.total_amount || '0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell style={{ paddingTop: 12, paddingBottom: 4 }}>
                            <Typography style={{ color: '#666', fontSize: 12 }}>
                              {new Date(invoice.due_date).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell style={{ paddingTop: 12, paddingBottom: 4 }}></TableCell>
                        </TableRow>
                        
                        {/* Payment Actions Row */}
                        <TableRow style={{ borderLeft: '3px solid #FFC107' }}>
                          <TableCell colSpan={4} style={{ paddingTop: 8, paddingBottom: 12 }}>
                            <Box>
                              <Typography style={{ fontSize: 11, fontWeight: 600, color: '#002D55', marginBottom: 8 }}>
                                Payment
                              </Typography>
                              <Box style={{ display: 'flex', gap: 8 }}>
                                {['accepted', 'challenged'].includes(invoice.status) ? (
                                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Typography style={{ color: '#2E7D32', fontWeight: 600, fontSize: 11 }}>
                                      ✓ Accepted for auto-pay
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      style={{ backgroundColor: '#4CAF50', color: '#fff', fontSize: 11, textTransform: 'none' }}
                                      onClick={() => handleAcceptInvoice(invoice.id)}
                                    >
                                      ✓ Accept
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      style={{ borderColor: '#F57C00', color: '#F57C00', fontSize: 11, textTransform: 'none' }}
                                      onClick={() => handleChallengeClick(invoice.id)}
                                    >
                                      ✗ Challenge
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      style={{ backgroundColor: '#002D55', color: '#fff', fontSize: 11, textTransform: 'none' }}
                                      onClick={() => handlePayInvoice(invoice.id)}
                                    >
                                      Pay Now (manually via Stripe)
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {false && (
                    <Box style={{ padding: 12, backgroundColor: '#FAFAFA', borderTop: '1px solid #eee' }}>
                      <Box style={{ display: 'flex', gap: 8 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          style={{ backgroundColor: '#002D55', color: '#fff' }}
                          onClick={() => handlePayInvoice(invoice.id)}
                        >
                          Pay Now
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

                    <Divider style={{ marginTop: 24, marginBottom: 24 }} />

          {/* ── Paid Invoices ── */}
          <Typography className={classes.cardHeaderSubTitle} style={{ fontSize: 'clamp(14px, 1.15vw, 18px)', marginBottom: 16 }}>
            Paid Invoices
          </Typography>
          {loadingData ? (
            <CircularProgress size={20} />
          ) : paidInvoices.length === 0 ? (
            <Typography style={{ color: '#999', fontStyle: 'italic' }}>
              No paid invoices yet.
            </Typography>
          ) : (
            <Box>
              {paidInvoicesByMonth.map((monthGroup, monthIdx) => (
                <Accordion key={monthGroup.key} defaultExpanded={monthIdx === 0} style={{ marginBottom: 12, borderRadius: 8, overflow: 'hidden' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f5f5f5' }}>
                    <Typography style={{ fontWeight: 600, color: '#002D55' }}>
                      {monthGroup.monthName} ({monthGroup.items.length} invoices)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails style={{ padding: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow style={{ backgroundColor: '#F0FFF0' }}>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Date & Time</TableCell>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Course</TableCell>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Service</TableCell>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Staff</TableCell>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Payer</TableCell>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Amount</TableCell>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }}>Invoice</TableCell>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }} align="center">Status</TableCell>
                            <TableCell style={{ fontWeight: 600, color: '#002D55', fontSize: 'clamp(10px, 0.82vw, 13px)' }} align="center">Payment</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthGroup.items.map((invoice) => (
                            <TableRow key={invoice.id} style={{ borderLeft: '3px solid #4CAF50' }}>
                              {(() => {
                                const appointment = getAppointmentByInvoice(invoice.id);
                                return (
                                  <>
                                    <TableCell>
                                      <Typography style={{ fontWeight: 600, fontSize: 'clamp(0.72rem, 0.76vw, 0.875rem)' }}>
                                        {appointment?.start_datetime ? formatDate(appointment.start_datetime) : '—'}
                                      </Typography>
                                      <Typography style={{ fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)', color: '#666' }}>
                                        {appointment?.start_datetime ? formatTime(appointment.start_datetime) : '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell style={{ fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)' }}>
                                      {appointment ? getCourseName(appointment.treatment) : '—'}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)' }}>
                                      {appointment ? (appointment.title || getAppointmentLabel(appointment.appointment_type)) : '—'}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)' }}>
                                      {appointment?.staff_member_name || '—'}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)' }}>
                                      {appointment ? (() => {
                                        const payerChip = getAppointmentPayer(appointment);
                                        return <Chip size="small" label={payerChip.label} style={payerChip.style} />;
                                      })() : '—'}
                                    </TableCell>
                                    <TableCell>
                                      <Typography style={{ fontWeight: 600, color: '#2E7D32', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)' }}>
                                        {invoice.currency || 'EUR'} {invoice.total_amount || '0.00'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Button
                                          onClick={() => handleViewInvoice(invoice.id)}
                                          style={{
                                            padding: 0,
                                            minWidth: 0,
                                            textTransform: 'none',
                                            color: '#002D55',
                                            fontWeight: 600,
                                            fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)',
                                          }}
                                        >
                                          #{invoice.invoice_number}
                                        </Button>
                                        <IconButton
                                          size="small"
                                          onClick={(event) => handleOpenInvoiceActions(event, invoice)}
                                          style={{ color: '#002D55' }}
                                        >
                                          <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                      {appointment ? (() => {
                                        const chip = getAppointmentStatusChip(appointment);
                                        return <Chip size="small" label={chip.label} style={chip.style} />;
                                      })() : (
                                        <Chip size="small" label={(invoice.status || 'Paid').toString()} style={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }} />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4, backgroundColor: '#E8F5E9', padding: '2px 8px', borderRadius: 12 }}>
                                        <Typography style={{ color: '#2E7D32', fontSize: 'clamp(9px, 0.72vw, 11px)', fontWeight: 600 }}>
                                          ✓ {invoice.payment_method
                                              ? invoice.payment_method.charAt(0).toUpperCase() + invoice.payment_method.slice(1)
                                              : 'Paid'}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                  </>
                                );
                              })()}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          <Divider style={{ marginTop: 24, marginBottom: 24 }} />

          {/* ── My Treatment Course Billing Summary ── */}
          {(() => {
            const courseMap = {};
            appointments.filter((appt) => {
              // Patient-paid only
              const payerRaw = String(appt.payer_type || appt.payer || 'patient').toLowerCase();
              return payerRaw !== 'insurance';
            }).forEach((appt) => {
              if (!appt.treatment) return;
              const key = String(appt.treatment);
              if (!courseMap[key]) {
                courseMap[key] = {
                  treatmentId: key,
                  courseName: getCourseName(appt.treatment),
                  total: 0,
                  paidTotal: 0,
                  unpaidTotal: 0,
                  count: 0,
                  services: new Set(),
                };
              }
              const inv = getAppointmentInvoice(appt.id);
              const amount = parseFloat(appt.booked_price || 0);
              courseMap[key].total += amount;
              courseMap[key].count += 1;
              courseMap[key].services.add(appt.title || appt.appointment_type || 'Appointment');
              if (inv && inv.status === 'paid') {
                courseMap[key].paidTotal += amount;
              } else if (inv && isOpenInvoice(inv)) {
                courseMap[key].unpaidTotal += amount;
              }
            });
            const courses = Object.values(courseMap);
            if (courses.length === 0) return null;
            return (
              <>
                <Typography className={classes.cardHeaderSubTitle} style={{ fontSize: 'clamp(14px, 1.15vw, 18px)', marginBottom: 16 }}>
                  My Billing by Course
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ backgroundColor: '#F0F8FF' }}>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Course</TableCell>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Services</TableCell>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="right">Appointments</TableCell>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="right">Total</TableCell>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="right">Paid</TableCell>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="right">Outstanding</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {courses.map((course) => (
                        <TableRow key={course.treatmentId} style={{ borderLeft: '3px solid #002D55' }}>
                          <TableCell style={{ fontWeight: 600, fontSize: 13, color: '#002D55' }}>{course.courseName}</TableCell>
                          <TableCell style={{ fontSize: 12, color: '#666' }}>{[...course.services].join(', ') || '—'}</TableCell>
                          <TableCell align="right" style={{ fontSize: 13 }}>{course.count}</TableCell>
                          <TableCell align="right" style={{ fontWeight: 600, fontSize: 13 }}>EUR {course.total.toFixed(2)}</TableCell>
                          <TableCell align="right" style={{ fontSize: 13, color: '#2E7D32' }}>EUR {course.paidTotal.toFixed(2)}</TableCell>
                          <TableCell align="right" style={{ fontSize: 13, color: course.unpaidTotal > 0 ? '#F57C00' : '#999' }}>EUR {course.unpaidTotal.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            );
          })()}

          <Divider style={{ marginTop: 24, marginBottom: 24 }} />

          {/* ── Subscription ── */}
          <Typography className={classes.cardHeaderSubTitle} style={{ fontSize: 'clamp(14px, 1.15vw, 18px)', marginBottom: 16 }}>
            Billing Portal
          </Typography>
          <Typography className={classes.bodyText}>
            Manage your billing details and view full history in the Stripe Billing Portal.
          </Typography>
          <Button
            className={classes.Button}
            variant="contained"
            fullWidth
            onClick={handleBillingPortal}
            disabled={!cardSaved}
            style={{ marginTop: 12 }}
          >
            Open Billing Portal
          </Button>

        </CardContent>
      </Card>

      {/* Payment Modal */}
      <AddPaymentMethodModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {/* Auto-Pay Consent Dialog */}
      <Dialog
        open={autoPayConsentOpen}
        onClose={() => setAutoPayConsentOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 18 } }}
      >
        <DialogTitle style={{ borderBottom: '1px solid #eee' }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 18, textTransform: 'uppercase' }}>
              Enable Auto-Pay
            </Typography>
            <IconButton
              onClick={() => setAutoPayConsentOpen(false)}
              size="small"
              className={classes.closeIconButton}
              style={{ borderRadius: 18 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          <Box style={{ backgroundColor: '#FFF8E1', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid #FFE0B2' }}>
            <Typography style={{ fontWeight: 600, color: '#F57C00', fontSize: 14, marginBottom: 8 }}>
              ⚠ Please review before enabling
            </Typography>
            <Typography style={{ color: '#333', fontSize: 13, lineHeight: 1.7 }}>
              By enabling auto-pay, you consent to having your saved payment method charged automatically each time an invoice is issued by your physiotherapist.
            </Typography>
          </Box>
          <Box style={{ padding: '8px 0' }}>
            <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 13, marginBottom: 12 }}>
              How it works:
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
              <Typography style={{ color: '#fb741a', fontWeight: 700, fontSize: 15 }}>•</Typography>
              <Typography style={{ color: '#333', fontSize: 13 }}>
                Your saved card will be charged automatically when new invoices are created.
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
              <Typography style={{ color: '#fb741a', fontWeight: 700, fontSize: 15 }}>•</Typography>
              <Typography style={{ color: '#333', fontSize: 13 }}>
                You will receive a copy of each invoice for your records.
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
              <Typography style={{ color: '#fb741a', fontWeight: 700, fontSize: 15 }}>•</Typography>
              <Typography style={{ color: '#333', fontSize: 13 }}>
                You can cancel auto-pay at any time by switching back to manual payment.
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Typography style={{ color: '#fb741a', fontWeight: 700, fontSize: 15 }}>•</Typography>
              <Typography style={{ color: '#333', fontSize: 13 }}>
                No card details are stored on our servers — all payments are processed securely by Stripe.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          <Button
            onClick={() => setAutoPayConsentOpen(false)}
            className={classes.closeIconButton}
            style={{ borderRadius: 18, padding: '6px 16px', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAutoPay}
            className={classes.Button}
          >
            I    — Enable Auto-Pay
          </Button>
        </Box>
      </Dialog>

      {/* Challenge Invoice Dialog */}
      <Dialog open={challengeDialogOpen} onClose={() => setChallengeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle style={{ color: '#002D55', fontWeight: 600 }}>Challenge Invoice</DialogTitle>
        <DialogContent>
          <Typography style={{ marginTop: 12, marginBottom: 16, color: '#666' }}>
            Please provide a reason for challenging this invoice. Your physiotherapist will review your case:
          </Typography>
          <textarea
            value={challengeReason}
            onChange={(e) => setChallengeReason(e.target.value)}
            placeholder="Describe the issue with this invoice..."
            style={{
              width: '100%',
              height: 120,
              padding: 12,
              borderRadius: 4,
              border: '1px solid #ddd',
              fontFamily: 'inherit',
              fontSize: 'clamp(11px, 0.85vw, 14px)',
              boxSizing: 'border-box'
            }}
          />
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          <Button
            variant="outlined"
            onClick={() => setChallengeDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#002D55', color: '#fff' }}
            onClick={handleSubmitChallenge}
          >
            Submit Challenge
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={unpaidFlowOpen}
        onClose={handleCloseUnpaidFlow}
        fullWidth
        maxWidth="md"
        PaperProps={{ style: { borderRadius: 18 } }}
      >
        <DialogTitle style={{ borderBottom: '1px solid #eee' }}>
          <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 18 }}>
            {unpaidFlowStep === 'overview' ? 'Unpaid Billed Appointments Overview' : unpaidFlowStep === 'checkout' ? 'Stripe Payment Checkout' : 'Payment Confirmation'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {unpaidFlowStep === 'overview' ? (
            <>
              <Typography style={{ color: '#666', marginBottom: 12 }}>
                Review appointments billed and due for payment before proceeding.
              </Typography>
              <Box style={{ marginBottom: 12, padding: 12, backgroundColor: '#FFF8E1', borderRadius: 8 }}>
                <Typography style={{ fontWeight: 600, color: '#F57C00' }}>
                  {unpaidBilledAppointments.length} appointment(s) · {unpaidInvoiceIdsForFlow.length} invoice(s)
                </Typography>
                <Typography style={{ fontSize: 16, fontWeight: 700, color: '#002D55' }}>
                  Total Due: EUR {unpaidAppointmentsTotal.toFixed(2)}
                </Typography>
              </Box>

              {unpaidBilledByMonth.length === 0 ? (
                <Typography style={{ color: '#999', fontStyle: 'italic' }}>
                  No unpaid billed appointments found.
                </Typography>
              ) : (
                unpaidBilledByMonth.map((monthGroup) => {
                  const monthTotal = monthGroup.items.reduce((sum, appt) => sum + (Number(appt.booked_price) || 0), 0);
                  return (
                    <Accordion key={monthGroup.key} style={{ marginBottom: 8 }} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f5f5f5' }}>
                        <Typography style={{ fontWeight: 600, color: '#002D55' }}>
                          {monthGroup.monthName} ({monthGroup.items.length}) · EUR {monthTotal.toFixed(2)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails style={{ padding: 0 }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow style={{ backgroundColor: '#F0F8FF' }}>
                                <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Date</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Service</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Staff</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Invoice</TableCell>
                                <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {monthGroup.items.map((appt) => {
                                const invoice = getAppointmentInvoice(appt.id);
                                return (
                                  <TableRow key={appt.id}>
                                    <TableCell>{formatDate(appt.start_datetime)} {formatTime(appt.start_datetime)}</TableCell>
                                    <TableCell>{appt.title || getAppointmentLabel(appt.appointment_type)}</TableCell>
                                    <TableCell>{appt.staff_member_name || '—'}</TableCell>
                                    <TableCell>
                                      {invoice?.invoice_number ? (
                                        <span
                                          onClick={() => handleViewInvoice(invoice.id)}
                                          style={{cursor: 'pointer', color: '#002D55', fontWeight: 600, textDecoration: 'underline'}}
                                        >
                                          #{invoice.invoice_number}
                                        </span>
                                      ) : (
                                        'Not invoiced'
                                      )}
                                    </TableCell>
                                    <TableCell align="right">{appt.booked_currency || 'EUR'} {(Number(appt.booked_price) || 0).toFixed(2)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </>
          ) : unpaidFlowStep === 'checkout' ? (
            <Elements key={paymentIntentSecret || 'stripe-checkout'} stripe={getStripePromise()} options={{ clientSecret: paymentIntentSecret }}>
              <StripeCheckoutForm
                paymentIntentSecret={paymentIntentSecret}
                amount={unpaidAppointmentsTotal}
                invoiceCount={unpaidInvoiceIdsForFlow.length}
                onSubmit={handleStripeCheckoutSubmit}
                loading={paymentProcessing}
              />
            </Elements>
          ) : (
            <>
              <Box style={{ padding: 20, textAlign: 'center', backgroundColor: '#F1F8E9', borderRadius: 8, marginBottom: 16 }}>
                <Typography style={{ fontSize: 32, color: '#2E7D32', marginBottom: 8 }}>✓</Typography>
                <Typography style={{ fontSize: 18, fontWeight: 600, color: '#2E7D32', marginBottom: 4 }}>
                  Payment Successful!
                </Typography>
                <Typography style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
                  {paymentConfirmation?.failedCount === 0
                    ? `All ${paymentConfirmation?.successCount} invoice(s) have been paid.`
                    : `${paymentConfirmation?.successCount} invoice(s) paid. ${paymentConfirmation?.failedCount} failed.`}
                </Typography>
              </Box>
              
              <Box style={{ padding: 12, backgroundColor: '#F5F9FF', borderRadius: 8, marginBottom: 16 }}>
                <Typography style={{ fontWeight: 600, color: '#002D55', marginBottom: 8 }}>Transaction Details</Typography>
                <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Box>
                    <Typography style={{ fontSize: 11, color: '#666' }}>Successful Payments</Typography>
                    <Typography style={{ fontSize: 18, fontWeight: 700, color: '#2E7D32' }}>
                      {paymentConfirmation?.successCount}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography style={{ fontSize: 11, color: '#666' }}>Failed</Typography>
                    <Typography style={{ fontSize: 18, fontWeight: 700, color: paymentConfirmation?.failedCount > 0 ? '#D32F2F' : '#2E7D32' }}>
                      {paymentConfirmation?.failedCount}
                    </Typography>
                  </Box>
                  <Box style={{ gridColumn: '1 / -1' }}>
                    <Typography style={{ fontSize: 11, color: '#666' }}>Total Amount Charged</Typography>
                    <Typography style={{ fontSize: 20, fontWeight: 700, color: '#002D55' }}>
                      EUR {unpaidAppointmentsTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box style={{ padding: 12, backgroundColor: '#FAFAFA', borderRadius: 8, marginBottom: 12 }}>
                <Typography style={{ fontWeight: 600, color: '#002D55', marginBottom: 8, fontSize: 12 }}>Invoice IDs</Typography>
                <Typography style={{ fontSize: 12, color: '#666', wordBreak: 'break-all' }}>
                  {paymentConfirmation?.invoiceIds.join(', ')}
                </Typography>
              </Box>

              <Typography style={{ fontSize: 11, color: '#999', marginBottom: 12 }}>
                Completed at: {paymentConfirmation?.timestamp ? new Date(paymentConfirmation.timestamp).toLocaleString() : '—'}
              </Typography>
            </>
          )}
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          <Button
            variant="outlined"
            onClick={handleCloseUnpaidFlow}
            disabled={processingUnpaidFlow}
            onMouseEnter={() => setUnpaidCancelHover(true)}
            onMouseLeave={() => setUnpaidCancelHover(false)}
            style={{ color: '#002D55', borderColor: '#ddd', borderRadius: 12 }}
          >
            {unpaidFlowStep === 'confirmation' ? 'Close' : 'Cancel'}
          </Button>
          {unpaidFlowStep === 'overview' ? (
            <Box
              onMouseEnter={() => setUnpaidProceedHover(true)}
              onMouseLeave={() => setUnpaidProceedHover(false)}
            >
              <Button
                variant="contained"
                onClick={handleProceedToStripeStep}
                disabled={unpaidInvoiceIdsForFlow.length === 0}
                style={{
                  backgroundColor: unpaidProceedHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY,
                  color: '#fff',
                  borderRadius: 12,
                  transition: 'background-color 0.2s'
                }}
              >
                Proceed to Stripe Checkout ({unpaidInvoiceIdsForFlow.length} invoices)
              </Button>
            </Box>
          ) : unpaidFlowStep === 'checkout' ? (
            <Box
              onMouseEnter={() => setUnpaidProceedHover(true)}
              onMouseLeave={() => setUnpaidProceedHover(false)}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  setUnpaidFlowStep('overview');
                  setPaymentIntentSecret(null);
                }}
                disabled={paymentProcessing}
                style={{
                  color: '#002D55',
                  borderColor: '#ddd',
                  borderRadius: 12
                }}
              >
                Back to Overview
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={handleDownloadReceipt}
              style={{
                backgroundColor: BRAND_COLORS.PRIMARY,
                color: '#fff',
                borderRadius: 12
              }}
            >
              Download Receipt
            </Button>
          )}
        </Box>
      </Dialog>

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkActionsAnchor}
        open={Boolean(bulkActionsAnchor)}
        onClose={() => setBulkActionsAnchor(null)}
      >
        <MenuItem 
          onClick={handleAcceptSelected}
          disabled={selectedInvoiceIds.length === 0}
        >
          Accept Selected ({selectedInvoiceIds.length})
        </MenuItem>
        <MenuItem 
          onClick={handleChallengeSelected}
          disabled={selectedInvoiceIds.length === 0}
        >
          Challenge Selected ({selectedInvoiceIds.length})
        </MenuItem>
      </Menu>

      {/* Appointment Details Dialog */}
      <Dialog
        open={appointmentDialogOpen}
        onClose={handleCloseAppointmentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 18 } }}
      >
        <DialogTitle style={{ borderBottom: '1px solid #eee', fontWeight: 600, color: '#002D55', fontSize: 18 }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 18, textTransform: 'uppercase' }}>Appointment Details</Typography>
            <IconButton
              onClick={handleCloseAppointmentDialog}
              size="small"
              className={classes.closeIconButton}
              style={{
                borderRadius: 18,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, overflow: 'hidden' }}>
          {selectedAppointment && (
            <Box>
              {selectedAppointment.status === 'cancelled' && (
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    marginBottom: '16px',
                    background: '#ffebee',
                    borderRadius: '8px',
                    gap: 8,
                  }}
                >
                  <Box style={{ color: '#d32f2f' }}>⚠</Box>
                  <Typography style={{ color: '#d32f2f', fontWeight: 600, fontSize: 14 }}>
                    This appointment has been cancelled
                  </Typography>
                </Box>
              )}
              {selectedAppointment.status !== 'cancelled' && selectedAppointmentIsPast && (
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    marginBottom: '16px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    gap: 8,
                  }}
                >
                  <Typography style={{ color: '#666', fontWeight: 600, fontSize: 14 }}>
                    Past appointments can no longer be cancelled.
                  </Typography>
                </Box>
              )}

              {/* Provider */}
              <Box style={{ marginBottom: 12 }}>
                <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Provider
                </Typography>
                <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                  {selectedAppointment.staff_member_name || 'Staff Member'}
                </Typography>
              </Box>

              {/* Service & Date Row */}
              <Box style={{ marginBottom: 12 }}>
                <Box style={{ display: 'flex', gap: 32 }}>
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Service
                    </Typography>
                    <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                      {selectedAppointment.title || getAppointmentLabel(selectedAppointment.appointment_type)}
                    </Typography>
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Date
                    </Typography>
                    <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                      {moment(selectedAppointment.start_datetime).format('YYYY-MM-DD')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Time & Amount Row */}
              <Box style={{ marginBottom: 12 }}>
                <Box style={{ display: 'flex', gap: 32 }}>
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Time
                    </Typography>
                    <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                      {moment(selectedAppointment.start_datetime).format('HH:mm')} - {moment(selectedAppointment.end_datetime).format('HH:mm')}
                    </Typography>
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Amount
                    </Typography>
                    <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                      {selectedAppointment.booked_currency || 'EUR'} {((selectedAppointment.booked_price == null ? 0 : Number(selectedAppointment.booked_price))).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Status & Payer Row */}
              <Box style={{ marginBottom: 12 }}>
                <Box style={{ display: 'flex', gap: 32 }}>
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Status
                    </Typography>
                    <Chip
                      label={selectedAppointment.status.charAt(0).toUpperCase() + selectedAppointment.status.slice(1)}
                      style={{
                        backgroundColor: selectedAppointment.status === 'cancelled' ? '#d32f2f' : selectedAppointment.status === 'completed' ? '#1565c0' : selectedAppointment.status === 'confirmed' ? '#2e7d32' : '#4ee63a',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                      size="small"
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Payer
                    </Typography>
                    {(() => {
                      const payerChip = getAppointmentPayer(selectedAppointment);
                      return (
                        <Chip
                          label={payerChip.label}
                          style={payerChip.style}
                          size="small"
                        />
                      );
                    })()}
                  </Box>
                </Box>
              </Box>

              {insuranceInfo && (
                <Box style={{ marginBottom: 12 }}>
                  <Box style={{ display: 'flex', gap: 32 }}>
                    <Box style={{ flex: 1 }}>
                      <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Insurance Provider
                      </Typography>
                      <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                        {insuranceInfo.provider || '—'}
                      </Typography>
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Plan
                      </Typography>
                      <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                        {insuranceInfo.planType || '—'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box style={{ marginTop: 8 }}>
                    <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Policy Number
                    </Typography>
                    <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                      {insuranceInfo.policyNumber || '—'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          {selectedAppointment && selectedAppointment.status !== 'cancelled' && !selectedAppointmentIsPast && (
            <>
              <Button
                variant="contained"
                onClick={handleCancelAppointment}
                disabled={cancellingAppointment || reschedulingAppointment}
                className={classes.closeIconButton}
              >
                {cancellingAppointment ? 'Cancelling...' : 'Cancel appointment'}
              </Button>
              <Box
                onMouseEnter={() => setRebokeButtonHover(true)}
                onMouseLeave={() => setRebokeButtonHover(false)}
              >
                <Button
                  variant="contained"
                  onClick={handleOpenRescheduleDialog}
                  disabled={reschedulingAppointment || cancellingAppointment}
                  className={classes.Button}
                >
                  Reschedule
                </Button>
              </Box>
            </>
          )}
          {selectedAppointment && selectedAppointment.status === 'cancelled' && (
            <Box
              onMouseEnter={() => setRebokeButtonHover(true)}
              onMouseLeave={() => setRebokeButtonHover(false)}
            >
              <Button
                variant="contained"
                onClick={handleOpenRescheduleDialog}
                disabled={reschedulingAppointment}
                style={{ backgroundColor: rebokeButtonHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY, color: '#fff', fontWeight: 500, borderRadius: 8, transition: 'background-color 0.2s' }}
              >
                Reschedule
              </Button>
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Reschedule Appointment Dialog */}
      <Dialog open={rescheduleDialogOpen} onClose={handleCloseRescheduleDialog} fullWidth maxWidth="sm" PaperProps={{ style: { borderRadius: 18 } }}>
        <DialogTitle style={{ borderBottom: '1px solid #eee'}}>
          <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 18, textTransform: 'uppercase' }}>Reschedule Appointment</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 24, overflow: 'auto' }}>
          {/* Select Service */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Service
          </Typography>
          <Box style={{ marginBottom: 24, position: 'relative' }}>
            <ArrowDropDownIcon
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: BRAND_COLORS.ACCENT,
                pointerEvents: 'none',
              }}
            />
            <select
              value={newAppointmentType}
              onChange={(e) => setNewAppointmentType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 34px',
                fontSize: 'clamp(11px, 0.85vw, 14px)',
                border: '1px solid #ddd',
                borderRadius: 8,
                fontFamily: 'Roboto, sans-serif',
                color: '#002D55',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'none',
              }}
            >
              <option value="">Select a service</option>
              {consultationServices.map((service) => (
                <option key={service.value} value={service.value}>
                  {service.label}
                </option>
              ))}
            </select>
          </Box>

          {/* Select Staff */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Staff Member (Optional)
          </Typography>
          <Box style={{ marginBottom: 24, position: 'relative' }}>
            <ArrowDropDownIcon
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: BRAND_COLORS.ACCENT,
                pointerEvents: 'none',
              }}
            />
            <select
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 34px',
                fontSize: 'clamp(11px, 0.85vw, 14px)',
                border: '1px solid #ddd',
                borderRadius: 8,
                fontFamily: 'Roboto, sans-serif',
                color: '#002D55',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'none',
              }}
            >
              <option value="">Any available staff</option>
              {staffMembers.map((staff) => (
                <option key={staff.user_id} value={staff.user_id}>
                  {staff.name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email || 'Staff Member'}
                </option>
              ))}
            </select>
          </Box>

          {/* Select New Date */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Select New Date
          </Typography>
          <Box style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 8,
            marginBottom: 24,
            padding: 12,
            backgroundColor: '#f5f5f5',
            borderRadius: 8,
          }}>
            {getNextNDays(28).map((date) => {
              const dateStr = moment(date).format('YYYY-MM-DD');
              const dayName = moment(date).format('ddd').toUpperCase();
              const dayNum = moment(date).format('D');
              const isSelected = dateStr === newAppointmentDate;
              const hasAppointments = dateHasAppointments(dateStr);
              return (
                <Button
                  key={dateStr}
                  onClick={() => {
                    setNewAppointmentDate(dateStr);
                    fetchAvailableTimes(dateStr);
                  }}
                  style={{
                    backgroundColor: isSelected ? '#002D55' : '#fff',
                    color: isSelected ? '#fff' : '#002D55',
                    border: hasAppointments ? '1px solid #fb741a' : (isSelected ? 'none' : '1px solid #ddd'),
                    padding: '8px 4px',
                    borderRadius: 8,
                    minHeight: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: isSelected ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                  }}
                >
                  <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography style={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>{dayNum}</Typography>
                    <Typography style={{ fontSize: 10, marginTop: 4, lineHeight: 1 }}>{dayName}</Typography>
                    {hasAppointments && (
                      <Box
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#fff' : '#fb741a',
                          marginTop: 4,
                        }}
                      />
                    )}
                  </Box>
                </Button>
              );
            })}
          </Box>

          {/* Select New Time */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Select New Time
          </Typography>
          {fetchingTimes ? (
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120, marginBottom: 24 }}>
              <CircularProgress style={{ color: '#002D55' }} size={40} />
            </Box>
          ) : (
            <Box style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 24,
            }}>
              {(availableTimes && availableTimes.length > 0 ? availableTimes : []).map((time) => (
                <Button
                  key={time}
                  onClick={() => setNewAppointmentTime(time)}
                  style={{
                    width: 'calc(33.333% - 6px)',
                    backgroundColor: newAppointmentTime === time ? '#002D55' : '#fff',
                    color: newAppointmentTime === time ? '#fff' : '#002D55',
                    border: newAppointmentTime === time ? 'none' : '1px solid #ddd',
                    borderRadius: 8,
                    padding: '10px 8px',
                    fontWeight: newAppointmentTime === time ? 600 : 500,
                    fontSize: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {time}
                </Button>
              ))}
            </Box>
          )}
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          <Button
            variant="outlined"
            onClick={handleCloseRescheduleDialog}
            disabled={reschedulingAppointment}
            style={{ color: '#002D55', borderColor: '#ddd', borderRadius: 8 }}
          >
            Cancel
          </Button>
          <Box
            onMouseEnter={() => setRescheduleConfirmHover(true)}
            onMouseLeave={() => setRescheduleConfirmHover(false)}
          >
            <Button
              variant="contained"
              style={{ backgroundColor: rescheduleConfirmHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY, color: '#fff', borderRadius: 8, transition: 'background-color 0.2s' }}
              onClick={handleRescheduleAppointment}
              disabled={reschedulingAppointment || !newAppointmentDate || !newAppointmentTime}
            >
              {reschedulingAppointment ? 'Rescheduling...' : 'Confirm'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Cancel Appointment Confirmation Dialog */}
      <Dialog open={cancelConfirmDialogOpen} onClose={() => setCancelConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ borderBottom: '1px solid #eee', paddingBottom: 16 }}>
          <Typography style={{ fontWeight: 600, color: '#d32f2f', fontSize: 18 }}>
            Cancel Appointment?
          </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 24 }}>
          <Typography style={{ color: '#333', marginBottom: 16 }}>
            Are you sure you want to cancel this appointment? If you want to reschedule instead, please use the rescheduling option to select a new date and time.
          </Typography>
          {selectedAppointment && (
            <Box style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <Typography style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                <strong>Date & Time:</strong> {selectedAppointment.start_datetime ? moment(selectedAppointment.start_datetime).format('MMMM D, YYYY [at] h:mm A') : 'N/A'}
              </Typography>
              {selectedAppointment.staff_member_name && (
                <Typography style={{ fontSize: 14, color: '#666' }}>
                  <strong>Practitioner:</strong> {selectedAppointment.staff_member_name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          <Button
            variant="outlined"
            onClick={() => setCancelConfirmDialogOpen(false)}
            disabled={cancellingAppointment}
            style={{ color: '#002D55', borderColor: '#ddd', borderRadius: 8 }}
          >
            Keep Appointment
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancelAppointment}
            disabled={cancellingAppointment}
            style={{ backgroundColor: '#fd483c', color: '#fff', fontWeight: 500, borderRadius: 8 }}
          >
            {cancellingAppointment ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </Box>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog open={invoicePreviewOpen} onClose={() => setInvoicePreviewOpen(false)} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 18 } }}>
        <DialogTitle style={{ borderBottom: '1px solid #eee', fontWeight: 600, color: '#002D55', fontSize: 18 }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 18, textTransform: 'uppercase' }}>Invoice Details</Typography>
            <IconButton
              onClick={() => setInvoicePreviewOpen(false)}
              size="small"
              className={classes.closeIconButton}
              style={{ borderRadius: 18 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{backgroundColor: '#FFF', padding: 0}}>
          {selectedInvoiceForPreview && (
            <InvoicePrintableContent
              invoice={selectedInvoiceForPreview}
              issuerName={selectedInvoiceForPreview.care_provider_name || 'VIRTUS PHYSIO'}
              clinic={{
                address: '',
                postalCode: '',
                city: '',
                cvr: '',
                bankAccount: '',
                phone: '',
                email: '',
              }}
              patient={{
                name: selectedInvoiceForPreview.patient?.name
                  || [selectedInvoiceForPreview.patient?.user?.first_name, selectedInvoiceForPreview.patient?.user?.last_name].filter(Boolean).join(' ').trim(),
                address: selectedInvoiceForPreview.patient?.address || '',
                postalCode: selectedInvoiceForPreview.patient?.zip || selectedInvoiceForPreview.patient?.postal_code || '',
                city: selectedInvoiceForPreview.patient?.city || '',
                email: selectedInvoiceForPreview.patient?.email || '',
              }}
            />
          )}
        </DialogContent>
        <DialogActions style={{padding: '16px'}}>
          <Button onClick={() => setInvoicePreviewOpen(false)} className={classes.closeIconButton} style={{ borderRadius: 18, padding: '6px 16px', fontWeight: 600 }}>
            Close
          </Button>
          <Button
            variant="contained"
            className={classes.Button}
            onClick={handleDownloadInvoicePDF}
            startIcon={<GetAppIcon />}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={invoiceActionsAnchorEl}
        open={Boolean(invoiceActionsAnchorEl)}
        onClose={handleCloseInvoiceActions}
      >
        <MenuItem
          onClick={() => {
            const invoiceId = invoiceActionsInvoice?.id;
            handleCloseInvoiceActions();
            if (invoiceId) {
              handleViewInvoice(invoiceId);
            }
          }}
        >
          <VisibilityIcon fontSize="small" style={{ marginRight: 8, color: '#002D55' }} />
          View Invoice
        </MenuItem>
      </Menu>
    </form>
  );
};

PaymentSubscription.propTypes = {
  className: PropTypes.string
};

export default PaymentSubscription;